import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { CompetitorImportTile } from '../components/insights/CompetitorImportTile.js';
import { CompetitorSweepTile } from '../components/insights/CompetitorSweepTile.js';
import { Wrench, Play, Eye, CheckCircle, XCircle, Loader2, ImageIcon, Database, ArrowRight, UserCheck, Network, RefreshCw, Trash2, Sparkles, Building2, Users, GitMerge, Link2, Scale, CalendarX, Layers } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import {
  listContactMergeCandidates,
  listCompanyMergeCandidates,
  listFundMergeCandidates,
} from '../lib/api/merge-candidates.js';
import {
  runBackfillImageHashes,
  runBackfillEntityAssociations,
  runBackfillHaltDocuments,
  runBackfillStuckResearchEvents,
  runHaltPurposeCalibration,
  runReextractHaltDocuments,
  runBackfillContactEmbeddings,
  runBackfillCompanyEmbeddings,
  runBackfillVisualMetrics,
  runRecomputeEligibility,
  runSweepAndPromote,
  runCleanupStaleSlugs,
  runRewriteTickerLedTitles,
  runPrismTaxonomyEval,
  runDiscoveryRerankBacktest,
  runReconcileSocialDrafts,
  runRepairMisdatedSignals,
  runRepairCollapsedSignals,
  runBackfillHeadlineCards,
  runBackfillCrossTickerCoParties,
  runBackfillIdentityChanges,
  runSeedSecuritiesMaster,
  runReverifyLinkedInUrns,
  runLinkedInFlagApiRecheck,
  runLinkedInPageAnalytics,
  runPipelineHealth,
  getToolRunStatus,
  getConsolidationCandidates,
  submitConsolidateEntityCoverage,
} from '../lib/api/tools.js';
import type {
  ConsolidationCandidate,
  ConsolidationProgress,
  ConsolidationSet,
} from '../lib/api/tools.js';
import type { ToolRunStatus } from '../lib/api/tools.js';
import { useWebSocket } from '../hooks/useWebSocket.js';
import { BacklogPanel } from '../components/BacklogPanel.js';
import { IssuerLinkRepairPanel } from '../components/IssuerLinkRepairPanel.js';
import { NoticeResolutionPanel } from '../components/NoticeResolutionPanel.js';
import { SameBoardMergePanel } from '../components/SameBoardMergePanel.js';
import { HubBoardPanel } from '../components/HubBoardPanel.js';
import { ExchangeReadinessPanel } from '../components/ExchangeReadinessPanel.js';
import {
  SubstanceRescorePreviewCard,
  SubstanceDistributionCheckCard,
} from '../components/SubstanceRecalibrationGate.js';
import { remediateTextImageHeroes } from '../lib/api/content.js';
import type { RemediateTextImageHeroesResult } from '../lib/api/content.js';
import {
  backfillCynapseBinding,
  enqueueCynapseBinding,
  getCynapseBindingProgress,
  triggerDriftSweep,
  getDriftSweepProgress,
  triggerCanonicalNormalisation,
  getCanonicalNormalisationProgress,
  triggerExternalCodesBackfill,
  getExternalCodesBackfillProgress,
  submitEntityMismatchAudit,
  getEntityMismatchAuditProgress,
  submitEntityMismatchResolve,
  type CynapseBindingProgress,
  type DriftSweepResult,
  type DriftSweepProgress,
  type CanonicalNormalisationResult,
  type CanonicalNormalisationProgress,
  type ExternalCodesBackfillResult,
  type ExternalCodesBackfillProgress,
  type EntityMismatchAuditResult,
  type EntityMismatchAuditProgress,
  type EntityMismatchResolveResult,
} from '../lib/api/entities.js';
import {
  backfillKeyRolePersonBindings,
  getKeyRolePersonBackfillProgress,
  runKeyRoleSuccession,
  healPersonEmbeddings,
  backfillPersonRoleHistory,
  linkPersonContacts,
  backfillContactEmployment,
  backfillRelationshipEdges,
  collapseIdentities,
  type CollapseIdentitiesResult,
  type RelationshipEdgesResult,
  type ContactEmploymentResult,
  regateMergeCandidates,
  runFirmHygiene,
  type FirmHygieneResult,
  runIrPrEdgeRepair,
  type IrPrEdgeRepairResult,
  type LinkPersonContactsResult,
  type RegateMergeCandidatesResult,
  type BackfillKeyRolePersonResult,
  type KeyRolePersonBackfillProgress,
} from '../lib/api/key-roles.js';
import type { KeyRoleSuccessionResult, HealPersonEmbeddingsResult } from '../lib/api/key-roles.js';
import type { BackfillCynapseBindingResult } from '../lib/api/entities.js';

interface ToolDefinition {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  run: (dryRun: boolean) => Promise<{ run_id: string }>;
  /**
   * Archive=true means the tool was built for a specific incident or migration
   * that has completed its primary purpose. Kept around for re-runnability +
   * reference but moved out of the default Active tab to reduce noise.
   */
  archive?: boolean;
}

const TOOLS: ToolDefinition[] = [
  {
    id: 'prism_taxonomy_eval',
    name: 'Marketfloor Gate · Prism Taxonomy Eval (§8)',
    description:
      'Verification gate for the §8 sector-taxonomy expansion (Consumer / ' +
      'Industrials / Utilities). Re-classifies a sample of recent announcements ' +
      'through the real Prism extraction path and diffs the new sector against ' +
      'the stored one — confirms the new sectors are assigned, existing buckets ' +
      "don't drift, and Energy→Utilities re-bucketing behaves. READ-ONLY: does " +
      'not re-write any history (one Gemini extraction per sampled announcement, ' +
      'so it takes a minute or two). Run this before relying on the new taxonomy; ' +
      'a low drift rate concentrated in the new sectors = pass.',
    icon: <Sparkles className="h-5 w-5" />,
    run: (_dryRun: boolean) => runPrismTaxonomyEval(12),
  },
  {
    id: 'discovery_rerank_backtest',
    name: 'Marketfloor Gate · Discovery Non-resource Lift Backtest (§1)',
    description:
      'Verification gate for the discovery non-resource lift, simulating the ' +
      'proposed broad_target_balance seed (additive boost 0.15) BEFORE it is ' +
      "applied (migration 188). Reconstructs marketfloor's candidate pool for " +
      'today with the live scorers, then compares the baseline ranking vs the ' +
      'lifted ranking. ADDITIVE model — resources are never demoted; the lift ' +
      'just helps quality non-resource stories compete. PASS = non-resource ' +
      'share rises modestly, every pick still clears the score floor, and the ' +
      'top picks stay a merit-based mix (resources still present). READ-ONLY: ' +
      'nothing is enqueued or published.',
    icon: <Database className="h-5 w-5" />,
    run: (_dryRun: boolean) => runDiscoveryRerankBacktest(),
  },
  {
    id: 'reconcile_social_drafts',
    name: 'Reconcile Social Drafts (cleanup)',
    description:
      'Combined social-draft cleanup. Regenerates frontmatter-leaked drafts ' +
      '(re-composes through the fixed stripper so `--- pageTitle: …` no longer ' +
      'leaks into the post body) and skips drafts whose article is archived ' +
      '(dead duplicate/superseded versions). Dry Run previews the counts; ' +
      'Execute applies. Only touches draft-status posts (never posted/hidden). ' +
      'Live-site Read-more links are realigned separately by the Sweep + Force ' +
      'Reslug tool.',
    icon: <RefreshCw className="h-5 w-5" />,
    run: (dryRun: boolean) => runReconcileSocialDrafts(dryRun),
  },
  {
    id: 'backfill_image_hashes',
    name: 'Backfill Image Hashes & Deduplicate',
    description:
      'Reads MD5 hashes from GCS blob metadata for all images without a content_hash, ' +
      'updates the Cynapse database, then removes duplicate image associations per announcement ' +
      '(keeping the oldest record with the most complete metadata).',
    icon: <ImageIcon className="h-5 w-5" />,
    run: (dryRun: boolean) => runBackfillImageHashes(dryRun),
  },
  {
    id: 'repair_misdated_signals',
    name: 'Repair Mis-dated Signals',
    description:
      "Clears intelligence_signals dated to a session they did not happen in. " +
      "Two lanes stamped event_date from the wall clock rather than the market " +
      "session — both fixed forward 2026-08-19; this clears what they wrote. " +
      "Dry-run reports each cohort separately: 42 holiday-dated scanner signals " +
      "RE-DATED from their linked scan row's real announcement_time (43 of the 47 " +
      "candidates are Friday filings lodged after 16:35, inside the after-close " +
      "hole — the only record we have of that window); 5 that cannot be re-dated " +
      "because the unique index on (signal_type, ticker, event_date) ignores " +
      "label; 139 exact duplicates; 926 price spikes created before the 10:00 " +
      "open that no live session ever confirmed; and 449 re-seen but still " +
      "carrying the previous day's exact value — justified by control, since " +
      "in-session rows match the prior day only 0.8% of the time against 64.5% " +
      "pre-open. 244 pre-open spikes that behave like live data are KEPT. " +
      "Deletes NULL announcement_scans.intelligence_signal_id first (NO ACTION " +
      "FK), permanently severing re-derivation — operator-ruled dead records. " +
      "Idempotent: a second run reports zeros.",
    icon: <CalendarX className="h-5 w-5" />,
    run: (dryRun: boolean) => runRepairMisdatedSignals(dryRun),
  },
  {
    id: 'repair_collapsed_signals',
    name: 'Repair Collapsed Signals',
    description:
      "Re-derives title signals that collapsed into one row per (type, ticker, " +
      "day). Until migration 336 the unique key on intelligence_signals was the " +
      "coarse signal_type, so same-day filings of different kinds (BC8 " +
      "2026-09-07: a Managing Director termination, a director appointment and " +
      "a final director's interest notice all map to regulatory_filing) " +
      "overwrote each other and only the last title written survived. Each " +
      "scan row still carries its own Stage-1 classification, so this writes " +
      "the signal it should have written, re-points the scan's FK, and binds " +
      "the new row to the same-day announcement whose title matches. Nothing " +
      "is deleted. Dry-run reports the cohort by Stage-1 type with a sample; " +
      "refuses to execute before migration 336 is applied. Idempotent.",
    icon: <Layers className="h-5 w-5" />,
    run: (dryRun: boolean) => runRepairCollapsedSignals(dryRun),
  },
  {
    id: 'backfill_headline_cards',
    name: 'Backfill Headline Cards (unpushed socials, stale cuts)',
    description:
      "Renders the per-article headline card for recent heroless articles whose " +
      "social posts have not yet been pushed to Buffer. The card hook reached the " +
      "worker generate path in backend rev 00922 (2026-09-16 01:22 UTC) after a " +
      "night of articles had been stored without one; cards never retroact and " +
      "the publishing side fetches media at push time, so every post still in the " +
      "stagger hold would ship the masthead default (33 articles / 66 posts when " +
      "measured). Selects recent, non-archived articles with no card and no " +
      "pickable image and at least one unpushed post, plus articles whose card " +
      "was rendered by an older template version (a typography change bumps it, " +
      "e.g. the Market Floor cut change of 2026-09-17), so queued posts never " +
      "ship a superseded cut; runs the one card entry point over them. Dry-run " +
      "lists the cohort split into missing vs stale. Idempotent.",
    icon: <ImageIcon className="h-5 w-5" />,
    run: (dryRun: boolean) => runBackfillHeadlineCards(dryRun),
  },
  {
    id: 'backfill_identity_changes',
    name: 'Identity Changes — replay renames & re-tickers (MTC → GGM class)',
    description:
      "A 'Change of Name and ASX Code' filing reads as procedural, so Stage-1 " +
      "dropped it — 13 of the last 18, including HTG. Nothing ever learned that " +
      "an issuer had been renamed or re-tickered, so the first filing under the " +
      "new code stubbed a SECOND company named from the discovery feed's stale " +
      "name for the recycled ticker (MetalsTech → Global Gold Mining became " +
      "'Gasgoyne Gold Mines Nl' on two mastheads). This replays the identity " +
      "filings the scanner already saw since 2026-04-29, pairs the two codes of " +
      "a re-ticker — the same filing is lodged under both on the same day — works " +
      "out which side holds the history, and raises ONE identity-change candidate " +
      "against that predecessor carrying new_code. Cynapse then closes the old " +
      "code's validity window and opens the new one against the same issuer. " +
      "Nothing is applied; every candidate is operator-approved. Dry-run lists " +
      "the events and the direction it would propose. Idempotent.",
    icon: <GitMerge className="h-5 w-5" />,
    run: (dryRun: boolean) => runBackfillIdentityChanges(dryRun),
  },
  {
    id: 'seed_securities_master',
    name: 'Seed Securities Master (current code window per issuer)',
    description:
      "The scanner already asks the temporal resolver who owns a ticker before " +
      "it stubs a new entity — a re-coded issuer is meant to bind to the existing " +
      "company. It never has, because the securities master is effectively empty " +
      "(36 rows across 19 issuers), so every lookup fails open. This writes one " +
      "CURRENT window per covered issuer, valid_from = its earliest filing, never " +
      "an invented date, so identity events have a window to close. auto_stub " +
      "entities are excluded on purpose: a stub sitting on a recycled code would " +
      "claim it and block the true owner. Idempotent — an existing window returns " +
      "created=false. A 422 one-owner conflict means another issuer holds that " +
      "code and is reported as a lineage finding, not a failure. 500 issuers per " +
      "run; re-run to continue.",
    icon: <Database className="h-5 w-5" />,
    run: (dryRun: boolean) => runSeedSecuritiesMaster(dryRun),
  },
  {
    id: 'backfill_cross_ticker_co_parties',
    name: 'Backfill Cross-Ticker Co-Parties (joint filings, both hubs)',
    description:
      "M&A Track C1 (2026-09-21). A joint filing the exchange tags under two " +
      "codes ingests once; the other party's copy dedupes onto it and, before " +
      "backend rev 00962, nothing recorded that the other party was a co-filer, " +
      "so the acquirer's hub never showed the deal (First Au / Javelin). Replays " +
      "recent duplicate_pdf cross-ticker results through the live record → " +
      "promote path: only joint-shaped classes (m_and_a, contract_award, " +
      "strategy_change) bind — a substantial holder's 604 tagged under the " +
      "holder's code, a multi-fund distribution notice or a bulk suspension " +
      "notice never does; the entity must be held and not the filer's own; a " +
      "back-source in flight on the key is left alone; no entity is created. " +
      "Then republishes published articles whose frontmatter predates the " +
      "co-party row (secondary_tickers renders at publish time) — lands on " +
      "TEST, promote when satisfied. Dry-run lists both cohorts. Idempotent.",
    icon: <GitMerge className="h-5 w-5" />,
    run: (dryRun: boolean) => runBackfillCrossTickerCoParties(dryRun),
  },
  {
    id: 'reverify_linkedin_urns',
    name: 'LinkedIn API recheck — fill page names, re-verify legacy URNs (B1 + B2)',
    description:
      "Milestone A (2026-09-20). Every verified LinkedIn URN that lacks a page " +
      "name, or was verified by the pre-evidence scraper with no verification " +
      "bundle (the BASF ≠ Bass Oil class), is looked up by its vanity slug on the " +
      "Organization Lookup API and re-run through the grounded verifier. " +
      "Corroborated rows get the API page name (a 30-day cache under LinkedIn's " +
      "storage rules), logo and follower count and an upgraded source; a " +
      "contradiction demotes the row to the review queue; inconclusive rows stay " +
      "verified tagged inconclusive_legacy. 100 rows per run, one finder call " +
      "each; stops cleanly when the day's finder budget (100/user) is spent and " +
      "resumes exactly there next run. Dry-run reports the remaining count. " +
      "Requires the LinkedIn connection (/settings/linkedin).",
    icon: <Link2 className="h-5 w-5" />,
    run: (dryRun: boolean) => runReverifyLinkedInUrns(dryRun),
  },
  {
    id: 'linkedin_flag_api_recheck',
    name: 'LinkedIn API recheck — flag slug-known review rows for the resolver (B3)',
    description:
      "Milestone A (2026-09-20). Review-queue rows that already carry a " +
      "/company/<slug> URL (666 when measured) are each one vanity lookup away " +
      "from a verdict. This flags up to 200 of them (coverage groups first) with " +
      "api_recheck_pending so the resolver's normal claim loop drains them at " +
      "the queue budget (~60/day on Dev Tier); each lands resolved or stays in " +
      "the queue with fresh, API-grounded evidence. Re-run to flag the next " +
      "batch. Dry-run counts the unflagged remainder. Synchronous.",
    icon: <Link2 className="h-5 w-5" />,
    run: (dryRun: boolean) => runLinkedInFlagApiRecheck(dryRun),
  },
  {
    id: 'linkedin_page_analytics',
    name: 'LinkedIn Page analytics — followers, Page views, post statistics',
    description:
      "P6 (2026-09-22). Collects the three masthead Pages' analytics from the " +
      "Community Management API: total followers, daily new followers (12 months " +
      "back), lifetime follower demographics, daily Page views plus lifetime views " +
      "by facet, and org-level post statistics (impressions, clicks, reactions). " +
      "18 calls a day. Dry Run is the spike: it calls LinkedIn and reports each " +
      "response's structure without writing anything. Execute enqueues a " +
      "collection run on the daily sweep's job (the first run backfills 12 " +
      "months). Raw LinkedIn figures are kept one year, as LinkedIn's storage " +
      "rules require; monthly totals are kept. No member data is requested. " +
      "Requires the LinkedIn connection (/settings/linkedin).",
    icon: <Link2 className="h-5 w-5" />,
    run: (dryRun: boolean) => runLinkedInPageAnalytics(dryRun),
  },
  {
    id: 'pipeline_health',
    name: 'Pipeline health — generated, held, duplicate pairs, guard skips',
    description:
      "The system measuring itself (2026-10-02). Per masthead per UTC day: " +
      "articles generated, articles held at generation (the gate's hold-rate " +
      "definition), duplicate (filing, product) pairs — 0 expected since backend " +
      "0.3.6 — and generations the one-article-per-pair guard skipped, by path. " +
      "Collected every day by the network-metrics sweep's pipeline lane into the " +
      "metrics table. Dry Run shows the last 7 days now without writing; Execute " +
      "queues a collection of the lane (the first run fills its history).",
    icon: <Layers className="h-5 w-5" />,
    run: (dryRun: boolean) => runPipelineHealth(dryRun),
  },
  {
    id: 'backfill_visual_metrics',
    name: 'Backfill Visual Metrics & Eligibility',
    description:
      "M1 B.3. Computes brightness, contrast, and Laplacian-variance sharpness " +
      "for the ~19,000 images uploaded before cynapse migration 014, then derives " +
      "hero/body eligibility flags + reason codes from those signals plus image_role " +
      "and caption. Downloads each image from GCS and runs scipy.ndimage.laplace — " +
      "~80 minutes for a full sweep. Also fixes up the small tail of images that " +
      "landed between the B.1 deploy and the B.2 wiring (eligibility-only pass, " +
      "no GCS roundtrip). Resumable — IS NULL predicates narrow on each run, so a " +
      "second run after completion is a no-op. After this lands, the publishing " +
      "service's featured-image picker and body-figure filter consult is_*_eligible " +
      "to suppress the near-black hero / regulatory-table figures that shipped on " +
      "themarketfloor.news 2026-06-04.",
    icon: <ImageIcon className="h-5 w-5" />,
    run: (dryRun: boolean) => runBackfillVisualMetrics(dryRun),
  },
  {
    id: 'recompute_eligibility',
    name: 'Recompute Image Eligibility',
    description:
      "Re-runs the hero/body/social gates over images that were ALREADY assessed, " +
      "using their stored signals. No GCS downloads, no AI — minutes, not hours. " +
      "The neighbouring visual-metrics backfill CANNOT do this: its eligibility " +
      "pass only looks at rows where a flag IS NULL, so an image marked eligible " +
      "under older thresholds is never revisited and gates added afterwards never " +
      "bind on it. That is why 1,248 hero-eligible rows still failed the 2026-08-18 " +
      "crop-retention gate a week later, and why two June A4 page composites were " +
      "still social-eligible on 2026-08-26 and went out as LinkedIn cards. Run this " +
      "after any eligibility rule change. Dry run reports the candidate count; a " +
      "real run reports how many images were demoted per tier.",
    icon: <ImageIcon className="h-5 w-5" />,
    run: (dryRun: boolean) => runRecomputeEligibility(dryRun),
  },
  {
    id: 'backfill_entity_associations',
    name: 'Backfill Entity → Image Associations',
    description:
      "Seeds cynapse.image_entity_associations from assayer.announcements.entity_id. " +
      "Until 2026-05-15 the PDF-extract path only wrote announcement associations, " +
      "leaving the entity-pool table empty in production despite 7,000+ extracted " +
      "images — so Phase 1's editorial selector found nothing per entity and articles " +
      "rendered with no hero image. Same-revision Cynapse fix populates the table " +
      "on new extracts; this tool replays the historical mapping for existing images. " +
      "Safe to run repeatedly — ON CONFLICT DO NOTHING.",
    icon: <ImageIcon className="h-5 w-5" />,
    run: (dryRun: boolean) => runBackfillEntityAssociations(dryRun),
  },
  {
    id: 'backfill_stuck_research_events',
    name: 'Replay Stuck M&A Research-Gate Events',
    description:
      'Walks deep_backsource_events stuck in state=saturating with 0 saturation jobs ' +
      '(events that opened before the Cynapse company-binder fix and never resolved ' +
      'counterparties). For each, runs the binder → Phase B → orchestrator chain so the ' +
      'event either reopens with populated counterparty_entity_ids or terminates clean. ' +
      'No-op if no stuck events remain.',
    icon: <Network className="h-5 w-5" />,
    run: (dryRun: boolean) => runBackfillStuckResearchEvents(dryRun, { limit: 50 }),
    archive: true,
  },
  {
    id: 'backfill_halt_documents',
    name: 'Fetch Halt Request Documents (E0)',
    description:
      'A trading halt states its own purpose — "pending an announcement … in relation to ' +
      'a potential change of control transaction" — and we have never read it: the halt ' +
      'signal is written from the title and the request letter is never fetched. Measured ' +
      '2026-08-18, 608 of 711 halt-class signals carry no announcement at all. This ' +
      'enqueues one document fetch per title-only halt or suspension signal, so the stated ' +
      'purpose becomes readable. No article is ever generated. Spend is one PDF fetch plus ' +
      'one Prism extraction each — runs in tranches of 100 and reports how many remain. ' +
      'Idempotent: a re-run skips anything already enqueued.',
    icon: <Network className="h-5 w-5" />,
    run: (dryRun: boolean) => runBackfillHaltDocuments(dryRun, { limit: 100 }),
  },
  {
    id: 'reextract_halt_documents',
    name: 'Re-extract Held Halt Documents (E1)',
    description:
      'The other half of the halt backfill. The E0 tool above fetches halt documents we ' +
      'do not have; this one re-runs extraction over the 46 we already hold, which were ' +
      'classified before the Stated-Purpose fields existed and would otherwise never ' +
      'carry a purpose — including the Ausgold request that started this work. Where a ' +
      'held document has no halt signal at all (28 of the 46), re-extraction creates ' +
      'one. One Prism classification call each; no article is generated. Run this and ' +
      'the E0 backfill before reading the calibration report.',
    icon: <Network className="h-5 w-5" />,
    run: (dryRun: boolean) => runReextractHaltDocuments(dryRun, { limit: 100 }),
  },
  {
    id: 'halt_purpose_calibration',
    name: 'Halt Stated-Purpose Calibration (E1)',
    description:
      'Read-only. Reports what the halt purpose extraction actually produced: the ' +
      'distribution of classes across every halt signal that has a document behind it, ' +
      'how many halts have no document at all, and every change_of_control hit with its ' +
      'verbatim span. That class is the one that opens a watch and auto-publishes, so it ' +
      'is checked by eye rather than by percentage. Nothing should rely on the class ' +
      'until this has been read. No AI calls, no writes.',
    icon: <Sparkles className="h-5 w-5" />,
    run: () => runHaltPurposeCalibration(),
  },
  {
    id: 'backfill_contact_embeddings',
    name: 'Backfill Contact Embeddings (Gemini)',
    description:
      'Generates Gemini text-embedding-004 vectors for the ~5,500 existing contacts in ' +
      'Cynapse global_entities that have NULL name_embedding (pre-dating the Pass 2 ' +
      'embed-on-ingest wiring). Loops the Cynapse backfill endpoint in batches of 200; ' +
      'per-batch progress streams via WebSocket. Resumable: rows already embedded are ' +
      'skipped by the WHERE clause, so re-running is a no-op. Vertex spend ~$0.02 for ' +
      'the full pass. After this completes, the matcher\'s embedding tier has full ' +
      'historical coverage and can find "T. Lowien" / "Mr T Lowien" / "Troy Lowien" ' +
      'as the same canonical record.',
    icon: <Sparkles className="h-5 w-5" />,
    run: (dryRun: boolean) => runBackfillContactEmbeddings(dryRun),
  },
  {
    id: 'backfill_company_embeddings',
    name: 'Backfill Company Embeddings (Gemini)',
    description:
      'Sibling of the contact backfill — operates on entity_type=\'company\' (~2,800 ' +
      'rows). Embeds each company on (canonical_name | industry) so future ' +
      'company-resolution paths can match by semantic similarity, not only by ASX code ' +
      'or pg_trgm name. Resumable, ~$0.01 spend for a full pass.',
    icon: <Building2 className="h-5 w-5" />,
    run: (dryRun: boolean) => runBackfillCompanyEmbeddings(dryRun),
  },
  {
    id: 'sweep_and_promote',
    name: 'Sweep-Refresh Live Articles',
    description:
      'Re-renders every article currently live on orecast, bellwire, and themarketfloor with ' +
      'the latest publishing-service code, and ships the changes to main as a single PR per pub. ' +
      'Use after a template or publishing-service change that needs to propagate to existing ' +
      'live pages — e.g. the SEO image-proxy URL rewrite, JSON-LD about.name, og:image fallback, ' +
      "slug word-boundary. Touches ONLY articles currently live on each pub's main branch — " +
      'test-only drafts under operator review are NOT affected, and the PR contains ONLY the ' +
      'swept article paths (not every commit sitting on test). Synchronous: each pub takes ' +
      '~30–120s while it renders, commits to test, then snapshots test→main with a single PR.',
    icon: <RefreshCw className="h-5 w-5" />,
    run: (dryRun: boolean) => runSweepAndPromote(dryRun),
  },
  {
    id: 'sweep_and_promote_reslug',
    name: 'Sweep-Refresh + Re-Optimise Slugs (one-time SEO migration)',
    description:
      'Same as Sweep-Refresh Live Articles, but additionally regenerates every article slug ' +
      'via the SEO-optimised generator (60-char cap, stops words / company suffixes stripped, ' +
      'numbers canonicalised). Every previous slug is preserved in the article_slugs history ' +
      'table and emitted as a 301 redirect in each pub\'s _redirects.json. ' +
      'Historical URLs point single-hop at the current one — no redirect chains. Use ONCE for ' +
      "the wholesale SEO migration; new articles use the optimised slug generator by default " +
      'thereafter. Dry-run shows per-pub article counts without changing anything.',
    icon: <RefreshCw className="h-5 w-5" />,
    run: (dryRun: boolean) => runSweepAndPromote(dryRun, { forceReslug: true }),
    archive: true,
  },
  {
    id: 'cleanup_stale_published_slugs',
    name: 'Cleanup Stale Published Slugs (pre-Stream-2 drift sweep)',
    description:
      'Walks every (announcement, content_product) pair generated since 2026-04-01 with more ' +
      'than one distinct published_slug. Identifies a canonical article per pair (most-recently-' +
      'published, fallback to most-recently-generated) and emits 301 redirects from every stale ' +
      'slug to the canonical via article_slugs alias rows; then triggers one sweep_and_promote ' +
      'per pub to ship a single PR per repo. Stale article rows are marked publish_status=' +
      'unpublished with an audit reason — subsequent dry-runs become no-ops. ' +
      'Widened from the original 2026-05-25 cascade window after the 2026-05-26 ' +
      'EMC Mt Edon bellwire build failure surfaced 67 pre-Stream-2 latent drift pairs ' +
      '(34 bellwire + 22 orecast + 11 marketfloor). Same tested code path; just a wider ' +
      'date window. Idempotent on the already-cleaned May 25 cascade pairs.',
    icon: <Trash2 className="h-5 w-5" />,
    run: (dryRun: boolean) => runCleanupStaleSlugs(dryRun, { since: '2026-04-01' }),
    archive: true,
  },
  {
    id: 'rewrite_ticker_led_titles',
    name: 'Rewrite ticker-led titles',
    description:
      'Reusable defence against model-side ticker-headline drift. Detects articles whose ' +
      'title leads with the ASX/NSX/LSE ticker (e.g. "RDM Column Leach Tests Deliver..." ' +
      'for entity Red Metal) and rewrites them to lead with editorial_preferred_name ' +
      '(fallback display_name). Excludes false positives where the title already starts ' +
      'with the company\'s display_name. For each match: rewrites the title, NULLs ' +
      'prepared_content, and queues republish-to-test (publish_reason=update_existing). ' +
      'Site-level promote (test → main) remains operator-triggered so this cleanup ' +
      'doesn\'t sweep up other in-flight work. Also regenerates social drafts attached to ' +
      'fixed articles so the tweet/LinkedIn body picks up the new title. Email subjects/' +
      'bodies use company_name not article_title, so already-sent emails contain no leaked ' +
      'text — no remediation needed there. Deterministic, no AI cost. Dry-run shows up to ' +
      '50 preview rewrites + total counts. ' +
      'Built for the 2026-05-26 leak regression (root cause: exchange_code missing from ' +
      'get_announcement projection, fixed in commit 9d341a673). Kept active in /tools ' +
      'because model preferences can drift again on any future vendor-side update — ' +
      'idempotent and safe to re-run; default since=2026-05-25 only re-detects newly leaked ' +
      'articles, never duplicates work on titles already fixed.',
    icon: <RefreshCw className="h-5 w-5" />,
    run: (dryRun: boolean) =>
      runRewriteTickerLedTitles(dryRun, { since: '2026-05-25', includeSocials: true }),
  },
];

type RunState = 'idle' | 'submitting' | 'running' | 'completed' | 'failed';

interface ToolState {
  runState: RunState;
  runId: string | null;
  dryRun: boolean;
  result: Record<string, any> | null;
  error: string | null;
  /** Latest `tool_progress` payload for the running job (2026-09-21) —
   *  tools that emit it (e.g. the LinkedIn recheck, every 5 rows) show a
   *  "processed X of Y · …" line instead of a bare spinner. */
  progress?: Record<string, any> | null;
}

/** "processed 45 of 60 · kept_grounded 40 · demoted 1" from a progress payload. */
function formatProgress(p: Record<string, any>): string {
  const parts: string[] = [];
  if (p.processed != null && p.total != null) parts.push(`processed ${p.processed} of ${p.total}`);
  for (const [k, v] of Object.entries(p)) {
    if (['run_id', 'tool_name', 'processed', 'total', 'status', 'dry_run'].includes(k)) continue;
    if (typeof v === 'number' && v !== 0) parts.push(`${k} ${v}`);
  }
  return parts.join(' \u00b7 ');
}

// localStorage key for persisting the active run_id per tool across
// navigations. ToolsPage component state lives only while the page is
// mounted; without this every visit shows a blank slate even if the
// backend is still running the job (or has finished and the user
// missed the WebSocket event).
const TOOL_RUNS_STORAGE_KEY = 'assayer:tools:runs';

interface StoredRun {
  runId: string;
  dryRun: boolean;
  submittedAt: number;  // epoch ms, for staleness pruning
}

function loadStoredRuns(): Record<string, StoredRun> {
  try {
    const raw = localStorage.getItem(TOOL_RUNS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    // Prune entries older than 24h — _active_tools is in-memory and
    // resets on backend deploy, so anything older is almost certainly
    // unreachable.
    const cutoff = Date.now() - 24 * 60 * 60 * 1000;
    const fresh: Record<string, StoredRun> = {};
    for (const [k, v] of Object.entries(parsed as Record<string, StoredRun>)) {
      if (v && typeof v.submittedAt === 'number' && v.submittedAt > cutoff) {
        fresh[k] = v;
      }
    }
    return fresh;
  } catch {
    return {};
  }
}

function persistStoredRuns(runs: Record<string, StoredRun>): void {
  try {
    localStorage.setItem(TOOL_RUNS_STORAGE_KEY, JSON.stringify(runs));
  } catch {
    // Quota / private-mode failures: silently drop. The backend run
    // continues; user just won't see resume state next visit.
  }
}

function rememberRun(toolId: string, runId: string, dryRun: boolean): void {
  const all = loadStoredRuns();
  all[toolId] = { runId, dryRun, submittedAt: Date.now() };
  persistStoredRuns(all);
}

function forgetRun(toolId: string): void {
  const all = loadStoredRuns();
  delete all[toolId];
  persistStoredRuns(all);
}


/**
 * Synchronous, bounded re-prepare of articles whose hero image is a PDF
 * page-render (a text-as-image hero). Distinct from the async TOOLS array —
 * this calls the synchronous /admin/content remediation endpoint directly and
 * processes a small batch per click.
 */
function CynapseBindingBackfill() {
  const [runState, setRunState] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const [mode, setMode] = useState<'dry' | 'exec' | 'enqueue'>('dry');
  const [result, setResult] = useState<BackfillCynapseBindingResult | null>(null);
  const [enqueueResult, setEnqueueResult] = useState<{ enqueued: number; by_type: Record<string, number>; note: string } | null>(null);
  const [progress, setProgress] = useState<CynapseBindingProgress | null>(null);
  const [polling, setPolling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch progress on mount and whenever polling is on. Cheap query — safe to poll.
  useEffect(() => {
    let cancelled = false;
    const fetchProgress = async () => {
      try {
        const p = await getCynapseBindingProgress();
        if (!cancelled) setProgress(p);
      } catch {
        // Best-effort; the panel stays usable without progress.
      }
    };
    fetchProgress();
    if (polling) {
      const id = setInterval(fetchProgress, 5000);
      return () => { cancelled = true; clearInterval(id); };
    }
    return () => { cancelled = true; };
  }, [polling]);

  const runDry = async () => {
    setRunState('running');
    setMode('dry');
    setError(null);
    setResult(null);
    setEnqueueResult(null);
    try {
      const data = await backfillCynapseBinding({ dry_run: true, limit: 10 });
      setResult(data);
      setRunState('done');
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Request failed');
      setRunState('error');
    }
  };

  const runSmallBatch = async () => {
    setRunState('running');
    setMode('exec');
    setError(null);
    setResult(null);
    setEnqueueResult(null);
    try {
      const data = await backfillCynapseBinding({ dry_run: false, limit: 50 });
      setResult(data);
      setRunState('done');
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Request failed');
      setRunState('error');
    }
  };

  const runAllAsync = async () => {
    if (!window.confirm(
      `Enqueue every unbound entity for background sync?\n\n` +
      `This flips cynapse_sync_status='pending' on every entity with cynapse_entity_id IS NULL ` +
      `(across both company-shape and fund-shape types). The CynapseCompanySyncWorker + ` +
      `CynapseFundSyncWorker drain the queue at ~6 entities/minute combined.\n\n` +
      `Progress shows in the panel below. Safe to leave running — no timeouts.`
    )) return;
    setRunState('running');
    setMode('enqueue');
    setError(null);
    setResult(null);
    setEnqueueResult(null);
    try {
      const data = await enqueueCynapseBinding();
      setEnqueueResult(data);
      setRunState('done');
      setPolling(true);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Request failed');
      setRunState('error');
    }
  };

  return (
    <div className="bg-surface border border-grey-200 rounded-lg p-6 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3 flex-1">
          <div className="mt-0.5 text-grey-500"><Link2 className="h-5 w-5" /></div>
          <div>
            <h3 className="font-semibold text-grey-900">Backfill Cynapse Entity Binding</h3>
            <p className="text-sm text-grey-600 mt-1">
              Binds <code className="text-xs bg-grey-100 px-1 rounded">entities WHERE cynapse_entity_id IS NULL</code> via Cynapse's
              resolve-by-name path. <strong>Dry Run</strong> previews 10 entities synchronously. <strong>Execute (50)</strong> runs a
              small batch synchronously (kept under the 30s budget). <strong>Run All in Background</strong> flips every unbound row to
              <code className="text-xs bg-grey-100 px-1 rounded mx-1">cynapse_sync_status='pending'</code> instantly — the existing
              CynapseCompanySyncWorker (company-shape) + CynapseFundSyncWorker (fund-shape, after mig 033+) drain the queue with no
              client-side wait. Progress polls every 5s.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap sm:ml-4 flex-shrink-0">
          <button
            onClick={runDry}
            disabled={runState === 'running'}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-grey-300 text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Eye className="h-4 w-4" />
            Dry Run
          </button>
          <button
            onClick={runSmallBatch}
            disabled={runState === 'running'}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-grey-300 text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Play className="h-4 w-4" />
            Execute (50)
          </button>
          <button
            onClick={runAllAsync}
            disabled={runState === 'running'}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Play className="h-4 w-4" />
            Run All in Background
          </button>
        </div>
      </div>

      {/* Live progress panel — always shown if we have any progress data */}
      {progress && (
        <div className="mt-4 pt-4 border-t border-grey-100">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-wide text-grey-500 font-semibold">Pipeline progress</div>
            <button
              onClick={() => setPolling(p => !p)}
              className={`text-xs px-2 py-1 rounded border transition-colors ${
                polling
                  ? 'border-primary-300 bg-primary-50 text-primary-700'
                  : 'border-grey-300 text-grey-700 hover:bg-grey-50'
              }`}
            >
              {polling ? (
                <span className="flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Polling</span>
              ) : 'Refresh on change'}
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 text-xs">
            <div className="bg-grey-50 rounded p-2"><div className="text-grey-500">Total</div><div className="font-mono font-semibold">{progress.total}</div></div>
            <div className="bg-success-50 rounded p-2"><div className="text-grey-500">Synced</div><div className="font-mono font-semibold text-success-700">{progress.synced}</div></div>
            <div className="bg-blue-50 rounded p-2"><div className="text-grey-500">Pending</div><div className="font-mono font-semibold text-blue-700">{progress.pending}</div></div>
            <div className="bg-amber-50 rounded p-2"><div className="text-grey-500">Syncing</div><div className="font-mono font-semibold text-amber-700">{progress.syncing}</div></div>
            <div className="bg-red-50 rounded p-2"><div className="text-grey-500">Failed</div><div className="font-mono font-semibold text-red-700">{progress.failed}</div></div>
            <div className="bg-purple-50 rounded p-2"><div className="text-grey-500">Merged</div><div className="font-mono font-semibold text-purple-700">{progress.merged}</div></div>
            <div className="bg-grey-50 rounded p-2"><div className="text-grey-500">Unbindable</div><div className="font-mono font-semibold text-grey-700">{progress.unbindable}</div></div>
          </div>
          {Object.keys(progress.by_type_still_unbound).length > 0 && (
            <div className="mt-2 text-xs">
              <div className="text-grey-500 mb-1">Still unbound, by type:</div>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(progress.by_type_still_unbound)
                  .sort((a, b) => b[1] - a[1])
                  .map(([t, n]) => (
                    <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 bg-grey-100 rounded font-mono">
                      <span className="text-grey-700">{t}</span>
                      <span className="text-grey-500">{n}</span>
                    </span>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Per-run output (dry-run / small-batch / enqueue confirm) */}
      {runState !== 'idle' && (
        <div className="mt-4 pt-4 border-t border-grey-100">
          {runState === 'running' && (
            <div className="flex items-center gap-2 text-sm text-primary-600">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>
                {mode === 'dry' ? 'Running dry run (10 entities)…'
                  : mode === 'exec' ? 'Running batch (50 entities, synchronous)…'
                  : 'Enqueuing all unbound rows for background sync…'}
              </span>
            </div>
          )}
          {runState === 'done' && enqueueResult && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-success-700">
                <CheckCircle className="h-4 w-4" />
                <span>Enqueued <span className="font-mono font-semibold">{enqueueResult.enqueued}</span> rows. Workers draining now — check progress above.</span>
              </div>
              {Object.keys(enqueueResult.by_type).length > 0 && (
                <div className="text-xs text-grey-600">
                  By type: {Object.entries(enqueueResult.by_type).map(([t, n]) => `${t}=${n}`).join(', ')}
                </div>
              )}
            </div>
          )}
          {runState === 'done' && result && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-success-700">
                <CheckCircle className="h-4 w-4" />
                <span>{mode === 'dry' ? 'Dry run complete' : 'Batch complete'}</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 text-xs">
                <div className="bg-grey-50 rounded p-2"><div className="text-grey-500">Processed</div><div className="font-mono font-semibold">{result.stats.processed}</div></div>
                <div className="bg-success-50 rounded p-2"><div className="text-grey-500">Bound</div><div className="font-mono font-semibold text-success-700">{result.stats.bound}</div></div>
                <div className="bg-amber-50 rounded p-2"><div className="text-grey-500">No match</div><div className="font-mono font-semibold text-amber-700">{result.stats.no_match}</div></div>
                <div className="bg-red-50 rounded p-2"><div className="text-grey-500">Error</div><div className="font-mono font-semibold text-red-700">{result.stats.error}</div></div>
                <div className="bg-grey-50 rounded p-2"><div className="text-grey-500">Skipped</div><div className="font-mono font-semibold">{result.stats.skipped}</div></div>
              </div>
              {result.error && (
                <div className="text-xs text-red-700">Service error: {result.error}</div>
              )}
              <pre className="text-xs bg-grey-50 rounded-md p-3 overflow-x-auto text-grey-800 font-mono max-h-96 overflow-y-auto">
                {JSON.stringify(result.examples, null, 2)}
              </pre>
            </div>
          )}
          {runState === 'error' && (
            <div className="flex items-center gap-2 text-sm text-red-600">
              <XCircle className="h-4 w-4" />
              <span>{error || 'Failed'}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function HealPersonEmbeddings() {
  const [runState, setRunState] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const [mode, setMode] = useState<'dry' | 'heal'>('dry');
  const [limit, setLimit] = useState('500');
  const [scope, setScope] = useState<'referenced' | 'all'>('referenced');
  const [result, setResult] = useState<HealPersonEmbeddingsResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async (m: 'dry' | 'heal') => {
    const n = Math.max(1, Math.min(2000, parseInt(limit, 10) || 500));
    if (m === 'heal' && !window.confirm(
      `Embed up to ${n} ${scope === 'all' ? 'unembedded persons' : 'unembedded persons that our bindings reference'}?\n\n` +
      `About ${Math.ceil(n / 25)} Vertex calls, each metered in ai_usage_log. Run a dry run first to see the characters that would be sent.`
    )) return;
    setRunState('running'); setMode(m); setError(null); setResult(null);
    try {
      const data = await healPersonEmbeddings({ dry_run: m !== 'heal', limit: n, scope });
      setResult(data);
      setRunState('done');
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Request failed');
      setRunState('error');
    }
  };

  return (
    <div className="bg-surface border border-grey-200 rounded-lg p-6 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3 flex-1">
          <div className="mt-0.5 text-grey-500"><Sparkles className="h-5 w-5" /></div>
          <div>
            <h3 className="font-semibold text-grey-900">Heal Person Embeddings</h3>
            <p className="text-sm text-grey-600 mt-1">
              Cynapse persons with no name vector are invisible to the resolver's embedding tiers, so every later
              filing that names one creates a duplicate (the June 2026 rows: 5,561 of them, 2,324 merged on 20 Sep).
              This embeds them. <strong>Referenced</strong> heals only persons that a key-role binding points at;
              <strong> all</strong> heals every unembedded person. Capped per run and idempotent — run it in slices.
              <strong> Dry run</strong> shows the candidates and characters before anything is sent.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap sm:ml-4 flex-shrink-0">
          <select value={scope} onChange={e => setScope(e.target.value as 'referenced' | 'all')}
            className="px-2 py-2 text-sm border border-grey-300 rounded-lg">
            <option value="referenced">Referenced</option>
            <option value="all">All</option>
          </select>
          <input value={limit} onChange={e => setLimit(e.target.value)} placeholder="Limit"
            className="px-2 py-2 text-sm border border-grey-300 rounded-lg w-20" />
          <button onClick={() => run('dry')} disabled={runState === 'running'}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-grey-300 text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            <Eye className="h-4 w-4" /> Dry run
          </button>
          <button onClick={() => run('heal')} disabled={runState === 'running'}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            <Play className="h-4 w-4" /> Heal
          </button>
        </div>
      </div>

      {runState !== 'idle' && (
        <div className="mt-4 pt-4 border-t border-grey-100">
          {runState === 'running' && (
            <div className="flex items-center gap-2 text-sm text-primary-600">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>{mode === 'heal' ? 'Embedding…' : 'Counting…'}</span>
            </div>
          )}
          {runState === 'done' && result && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-success-700">
                <CheckCircle className="h-4 w-4" />
                <span>{result.dry_run ? 'Dry run complete' : 'Heal complete'}</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 text-xs">
                <div className="bg-grey-50 rounded p-2"><div className="text-grey-500">Unembedded (all)</div><div className="font-mono font-semibold">{result.unembedded_total ?? '—'}</div></div>
                <div className="bg-grey-50 rounded p-2"><div className="text-grey-500">Referenced</div><div className="font-mono font-semibold">{result.referenced ?? '—'}</div></div>
                <div className="bg-amber-50 rounded p-2"><div className="text-grey-500">Candidates this run</div><div className="font-mono font-semibold text-amber-700">{result.candidates}</div></div>
                <div className="bg-grey-50 rounded p-2"><div className="text-grey-500">Characters</div><div className="font-mono font-semibold">{result.chars.toLocaleString()}</div></div>
                <div className="bg-success-50 rounded p-2"><div className="text-grey-500">Healed</div><div className="font-mono font-semibold text-success-700">{result.healed}</div></div>
                <div className="bg-grey-50 rounded p-2"><div className="text-grey-500">Failed</div><div className="font-mono font-semibold">{result.failed}</div></div>
              </div>
              <div className="text-xs text-grey-600">
                {result.batches} Vertex call{result.batches === 1 ? '' : 's'}{result.model ? ` · ${result.model}` : ''} · metered in ai_usage_log (provider vertex, phase person_embedding_heal)
              </div>
            </div>
          )}
          {runState === 'error' && (
            <div className="flex items-center gap-2 text-sm text-error-700">
              <XCircle className="h-4 w-4" />
              <span>{error}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PersonRoleHistoryBackfill() {
  const [runState, setRunState] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const [mode, setMode] = useState<'dry' | 'apply'>('dry');
  const [codes, setCodes] = useState('');
  const [progress, setProgress] = useState({ chunks: 0, issuers: 0, bindings: 0 });
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [error, setError] = useState<string | null>(null);

  const run = async (m: 'dry' | 'apply') => {
    const codeList = codes.trim().toUpperCase();
    if (m === 'apply' && !window.confirm(
      `Write person_role_history rows in Cynapse${codeList ? ` for ${codeList}` : ' for every issuer with bound key roles'}?\n\n` +
      `Database-only (no model calls) and idempotent. Run a dry run first.`
    )) return;
    setRunState('running'); setMode(m); setError(null); setCounts({});
    setProgress({ chunks: 0, issuers: 0, bindings: 0 });
    try {
      // A durable job on the server: it survives a deploy and resumes from
      // its cursor. Progress arrives by polling the job row.
      const result = await backfillPersonRoleHistory({ dry_run: m !== 'apply', codes: codeList || undefined }, st => {
        const p = st.progress || {};
        setProgress({ chunks: 0, issuers: p.done ?? 0, bindings: p.counts?.insert ?? 0 });
        if (p.counts) setCounts({ ...p.counts });
      });
      setProgress({ chunks: result.chunks ?? 0, issuers: result.issuers ?? 0, bindings: 0 });
      setCounts({ ...result.counts });
      setRunState('done');
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Request failed');
      setRunState('error');
    }
  };

  const order = ['insert', 'extend', 'close', 'touch', 'skipped_change_type', 'skipped_not_person', 'skipped_no_company', 'error'];
  const shown = [...order.filter(k => k in counts), ...Object.keys(counts).filter(k => !order.includes(k))];

  return (
    <div className="bg-surface border border-grey-200 rounded-lg p-6 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3 flex-1">
          <div className="mt-0.5 text-grey-500"><Users className="h-5 w-5" /></div>
          <div>
            <h3 className="font-semibold text-grey-900">Person Role History</h3>
            <p className="text-sm text-grey-600 mt-1">
              Replays every bound key role into Cynapse's role history (person × company × role, with tenure dates
              from the filing), so the resolver can match a returning director by name and board instead of relying on
              embeddings. Database-only — no model calls, no person is created. Idempotent, so any run can be repeated;
              historical mentions are skipped. Runs on the server in the background; this card follows its progress.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap sm:ml-4 flex-shrink-0">
          <input value={codes} onChange={e => setCodes(e.target.value)} placeholder="Tickers (optional)"
            className="px-2 py-2 text-sm border border-grey-300 rounded-lg w-36" />
          <button onClick={() => run('dry')} disabled={runState === 'running'}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-grey-300 text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            <Eye className="h-4 w-4" /> Dry run
          </button>
          <button onClick={() => run('apply')} disabled={runState === 'running'}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            <Play className="h-4 w-4" /> Apply
          </button>
        </div>
      </div>

      {runState !== 'idle' && (
        <div className="mt-4 pt-4 border-t border-grey-100 space-y-2">
          {runState === 'running' && (
            <div className="flex items-center gap-2 text-sm text-primary-600">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>{mode === 'apply' ? 'Writing…' : 'Counting…'} runs on the server; chunk {progress.chunks + 1}</span>
            </div>
          )}
          {runState === 'done' && (
            <div className="flex items-center gap-2 text-sm text-success-700">
              <CheckCircle className="h-4 w-4" />
              <span>{mode === 'apply' ? 'Backfill complete' : 'Dry run complete (nothing written)'}</span>
            </div>
          )}
          {runState === 'error' && (
            <div className="flex items-center gap-2 text-sm text-error-700">
              <XCircle className="h-4 w-4" />
              <span>{error}. Safe to run again (idempotent).</span>
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 text-xs">
            <div className="bg-grey-50 rounded p-2"><div className="text-grey-500">Chunks</div><div className="font-mono font-semibold">{progress.chunks}</div></div>
            <div className="bg-grey-50 rounded p-2"><div className="text-grey-500">Issuers</div><div className="font-mono font-semibold">{progress.issuers.toLocaleString()}</div></div>
            <div className="bg-grey-50 rounded p-2"><div className="text-grey-500">Bindings</div><div className="font-mono font-semibold">{progress.bindings.toLocaleString()}</div></div>
            {shown.map(k => (
              <div key={k} className="bg-grey-50 rounded p-2"><div className="text-grey-500">{k.replace(/_/g, ' ')}</div><div className="font-mono font-semibold">{counts[k].toLocaleString()}</div></div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function PersonContactLinks() {
  const [runState, setRunState] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const [result, setResult] = useState<LinkPersonContactsResult | null>(null);
  const [linkProgress, setLinkProgress] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const run = async (apply: boolean) => {
    if (apply && !window.confirm(
      'Link Cynapse contacts to the person they are?\n\n' +
      'Pairs come from a key-role row holding both ids, or a contact and a key role in the same filing, ' +
      'and each must pass the same-person name check. Nothing is merged or deleted. Run a dry run first.'
    )) return;
    setRunState('running'); setError(null); setResult(null); setLinkProgress(null);
    try {
      setResult(await linkPersonContacts({ dry_run: !apply, keys: 'AB' }, st => {
        const p = st.progress || {};
        if (p.total) setLinkProgress({ done: p.done ?? 0, total: p.total });
      }));
      setRunState('done');
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Request failed');
      setRunState('error');
    }
  };
  return (
    <div className="bg-surface border border-grey-200 rounded-lg p-6 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3 flex-1">
          <div className="mt-0.5 text-grey-500"><Link2 className="h-5 w-5" /></div>
          <div>
            <h3 className="font-semibold text-grey-900">Link Contacts to Persons</h3>
            <p className="text-sm text-grey-600 mt-1">
              The same human exists as a person (filings, role history) and as a contact (announcement contact lines,
              emails). This links them on two exact keys, a key-role row holding both ids or the same filing, and
              only when the names are the same person. Nothing is merged or deleted, and it is safe to repeat.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap sm:ml-4 flex-shrink-0">
          <button onClick={() => run(false)} disabled={runState === 'running'}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-grey-300 text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            <Eye className="h-4 w-4" /> Dry run
          </button>
          <button onClick={() => run(true)} disabled={runState === 'running'}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            <Play className="h-4 w-4" /> Link
          </button>
        </div>
      </div>
      {runState !== 'idle' && (
        <div className="mt-4 pt-4 border-t border-grey-100 space-y-2">
          {runState === 'running' && (
            <div className="flex items-center gap-2 text-sm text-primary-600"><Loader2 className="h-4 w-4 animate-spin" />
              <span>Running on the server{linkProgress ? `: ${linkProgress.done.toLocaleString()} of ${linkProgress.total.toLocaleString()}` : '…'}</span></div>
          )}
          {runState === 'error' && (
            <div className="flex items-center gap-2 text-sm text-error-700"><XCircle className="h-4 w-4" /><span>{error}</span></div>
          )}
          {runState === 'done' && result && (
            <>
              <div className="flex items-center gap-2 text-sm text-success-700">
                <CheckCircle className="h-4 w-4" />
                <span>{result.dry_run ? 'Dry run complete (nothing written)' : 'Links written'} · {result.pairs_in.toLocaleString()} binding pairs, {result.filings_in.toLocaleString()} filings</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 text-xs">
                {Object.entries(result.counts).map(([k, v]) => (
                  <div key={k} className="bg-grey-50 rounded p-2"><div className="text-grey-500">{k.replace(/_/g, ' ')}</div><div className="font-mono font-semibold">{v.toLocaleString()}</div></div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

const EMPLOYMENT_LABEL: Record<string, string> = {
  lines: 'Contact lines', firm_issuer_internal: "Issuer's own staff", firm_issuer_domain: "Issuer's email domain",
  firm_domain: 'Firm by email domain', firm_name_exact: 'Firm by exact name', firm_domain_create: 'New firm (by domain)',
  firm_would_create: 'Would create firm', firm_unresolved: 'Firm not resolved', firm_no_organisation: 'No organisation', firm_client_named: 'Names the client, no email',
  edge_insert: 'New employment', edge_extend: 'Employment extended back', edge_touch: 'Employment re-seen',
  skipped_no_date: 'No filing date', error: 'Errors',
};

function ContactEmployment() {
  const [runState, setRunState] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const [result, setResult] = useState<ContactEmploymentResult | null>(null);
  const [prog, setProg] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const run = async (apply: boolean) => {
    if (apply && !window.confirm(
      'Resolve the firm on every announcement contact line and record who works where?\n\n' +
      "Firms resolve to the issuer (its own staff), the owner of the work email domain, or an exact existing name; " +
      'a new firm is created only for an email domain nobody owns. No research calls. Run a dry run first.'
    )) return;
    setRunState('running'); setError(null); setResult(null); setProg(null);
    try {
      setResult(await backfillContactEmployment({ dry_run: !apply }, st => {
        const p = st.progress || {};
        if (p.total) setProg({ done: p.done ?? 0, total: p.total });
        if (p.counts) setResult({ dry_run: !apply, filings: p.total ?? 0, counts: { ...p.counts } });
      }));
      setRunState('done');
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Request failed');
      setRunState('error');
    }
  };
  return (
    <div className="bg-surface border border-grey-200 rounded-lg p-6 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3 flex-1">
          <div className="mt-0.5 text-grey-500"><Building2 className="h-5 w-5" /></div>
          <div>
            <h3 className="font-semibold text-grey-900">Who Works Where (contact lines)</h3>
            <p className="text-sm text-grey-600 mt-1">
              Resolves the firm named on every announcement contact line (the issuer for its own staff, otherwise the
              company that owns the work email domain or an exact existing name) and records that the person works there,
              dated by the filing. A firm is created only for an email domain nobody owns. No research calls; safe to repeat.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap sm:ml-4 flex-shrink-0">
          <button onClick={() => run(false)} disabled={runState === 'running'}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-grey-300 text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            <Eye className="h-4 w-4" /> Dry run
          </button>
          <button onClick={() => run(true)} disabled={runState === 'running'}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            <Play className="h-4 w-4" /> Apply
          </button>
        </div>
      </div>
      {runState !== 'idle' && (
        <div className="mt-4 pt-4 border-t border-grey-100 space-y-2">
          {runState === 'running' && (
            <div className="flex items-center gap-2 text-sm text-primary-600"><Loader2 className="h-4 w-4 animate-spin" />
              <span>Running on the server{prog ? `: ${prog.done.toLocaleString()} of ${prog.total.toLocaleString()} filings` : '…'}</span></div>
          )}
          {runState === 'error' && <div className="flex items-center gap-2 text-sm text-error-700"><XCircle className="h-4 w-4" /><span>{error}</span></div>}
          {runState === 'done' && (
            <div className="flex items-center gap-2 text-sm text-success-700"><CheckCircle className="h-4 w-4" />
              <span>{result?.dry_run ? 'Dry run complete (nothing written)' : 'Written'}</span></div>
          )}
          {result && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              {Object.entries(result.counts).map(([k, v]) => (
                <div key={k} className="bg-grey-50 rounded p-2"><div className="text-grey-500">{EMPLOYMENT_LABEL[k] ?? k}</div><div className="font-mono font-semibold">{v.toLocaleString()}</div></div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const EDGE_LABEL: Record<string, string> = {
  insert: 'New relationships', touch: 'Re-seen', extend: 'Extended back', would_write: 'Would record',
  resolved_by_name: 'Firm matched by name', unresolved_name: 'Firm name not matched',
  skipped_type: 'Role not promoted', skipped_incomplete: 'Missing a side', skipped_no_date: 'No filing date',
};

function RelationshipEdges() {
  const [runState, setRunState] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const [result, setResult] = useState<RelationshipEdgesResult | null>(null);
  const [prog, setProg] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const run = async (apply: boolean) => {
    if (apply && !window.confirm(
      'Record who acts for whom, from what filings already say?\n\n' +
      'Only roles that state a relationship are used (adviser, auditor, lead manager, JV partner, customer, IR/PR). ' +
      'A firm named in the M&A adviser field is matched to an existing company and never creates one. Run a dry run first.'
    )) return;
    setRunState('running'); setError(null); setResult(null); setProg(null);
    try {
      setResult(await backfillRelationshipEdges({ dry_run: !apply }, st => {
        const p = st.progress || {};
        if (p.total) setProg({ done: p.done ?? 0, total: p.total });
        if (p.counts) setResult({ dry_run: !apply, edges: p.total ?? 0, counts: { ...p.counts } });
      }));
      setRunState('done');
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Request failed');
      setRunState('error');
    }
  };
  return (
    <div className="bg-surface border border-grey-200 rounded-lg p-6 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3 flex-1">
          <div className="mt-0.5 text-grey-500"><Network className="h-5 w-5" /></div>
          <div>
            <h3 className="font-semibold text-grey-900">Who Acts For Whom</h3>
            <p className="text-sm text-grey-600 mt-1">
              Turns what filings already say into dated relationships: advisers, auditors, lead managers, JV partners,
              customers and IR/PR firms, from the companies named on each filing and from the M&amp;A adviser lists.
              Each is kept with its first and last sighting, so "currently acts for" is answerable as of any date.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap sm:ml-4 flex-shrink-0">
          <button onClick={() => run(false)} disabled={runState === 'running'}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-grey-300 text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            <Eye className="h-4 w-4" /> Dry run
          </button>
          <button onClick={() => run(true)} disabled={runState === 'running'}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            <Play className="h-4 w-4" /> Apply
          </button>
        </div>
      </div>
      {runState !== 'idle' && (
        <div className="mt-4 pt-4 border-t border-grey-100 space-y-2">
          {runState === 'running' && (
            <div className="flex items-center gap-2 text-sm text-primary-600"><Loader2 className="h-4 w-4 animate-spin" />
              <span>Running on the server{prog ? `: ${prog.done.toLocaleString()} of ${prog.total.toLocaleString()}` : '…'}</span></div>
          )}
          {runState === 'error' && <div className="flex items-center gap-2 text-sm text-error-700"><XCircle className="h-4 w-4" /><span>{error}</span></div>}
          {runState === 'done' && (
            <div className="flex items-center gap-2 text-sm text-success-700"><CheckCircle className="h-4 w-4" />
              <span>{result?.dry_run ? 'Dry run complete (nothing written)' : 'Recorded'}</span></div>
          )}
          {result && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              {Object.entries(result.counts).map(([k, v]) => (
                <div key={k} className="bg-grey-50 rounded p-2"><div className="text-grey-500">{EDGE_LABEL[k] ?? k}</div><div className="font-mono font-semibold">{v.toLocaleString()}</div></div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CollapseIdentities() {
  const [runState, setRunState] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const [result, setResult] = useState<CollapseIdentitiesResult | null>(null);
  const [prog, setProg] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const run = async (apply: boolean) => {
    if (apply && !window.confirm(
      'Merge person records whose sources agree?\n\n' +
      'Only where they share a personal email address, or share an employing firm and have an identical name. ' +
      'Shared office mailboxes never count. Each merge keeps the oldest record, moves its roles and filings, ' +
      'and is reversible from the archive. Run a dry run first.'
    )) return;
    setRunState('running'); setError(null); setResult(null); setProg(null);
    try {
      setResult(await collapseIdentities({ dry_run: !apply }, st => {
        const p = st.progress || {};
        if (p.total) setProg({ done: p.done ?? 0, total: p.total });
      }));
      setRunState('done');
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Request failed');
      setRunState('error');
    }
  };
  return (
    <div className="bg-surface border border-grey-200 rounded-lg p-6 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3 flex-1">
          <div className="mt-0.5 text-grey-500"><GitMerge className="h-5 w-5" /></div>
          <div>
            <h3 className="font-semibold text-grey-900">Collapse Identities (where the sources agree)</h3>
            <p className="text-sm text-grey-600 mt-1">
              Merges person records that a shared personal email, or the same employing firm plus an identical name,
              proves are one human — the common case of a company secretary or adviser held once per board. Shared
              office mailboxes, names that fail the same-person check, and pairs you marked as different are left alone
              for the Identities tab. Every merge is reversible from the archive.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap sm:ml-4 flex-shrink-0">
          <button onClick={() => run(false)} disabled={runState === 'running'}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-grey-300 text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            <Eye className="h-4 w-4" /> Dry run
          </button>
          <button onClick={() => run(true)} disabled={runState === 'running'}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            <Play className="h-4 w-4" /> Merge
          </button>
        </div>
      </div>
      {runState !== 'idle' && (
        <div className="mt-4 pt-4 border-t border-grey-100 space-y-2 text-sm">
          {runState === 'running' && (
            <div className="flex items-center gap-2 text-primary-600"><Loader2 className="h-4 w-4 animate-spin" />
              <span>Running on the server{prog ? `: ${prog.done} of ${prog.total} groups` : '…'}</span></div>
          )}
          {runState === 'error' && <div className="flex items-center gap-2 text-error-700"><XCircle className="h-4 w-4" /><span>{error}</span></div>}
          {runState === 'done' && result && (
            <>
              <div className="flex items-center gap-2 text-success-700"><CheckCircle className="h-4 w-4" />
                <span>{result.dry_run
                  ? `${result.total_groups.toLocaleString()} group${result.total_groups === 1 ? '' : 's'} qualify (nothing merged)`
                  : `${result.merged.toLocaleString()} merged · ${result.persons_removed.toLocaleString()} records folded in · ${result.bindings_rebound.toLocaleString()} filings re-pointed${result.failed ? ` · ${result.failed} failed` : ''}`}</span></div>
              {result.skipped && (
                <div className="text-xs text-grey-600">
                  Left for review: {(result.skipped.name_gate ?? 0).toLocaleString()} name mismatch ·
                  {' '}{(result.skipped.role_mailbox ?? 0).toLocaleString()} shared office mailbox ·
                  {' '}{(result.skipped.decided_different ?? 0).toLocaleString()} marked different
                </div>
              )}
              {result.examples?.length > 0 && (
                <ul className="text-xs text-grey-600 space-y-0.5">
                  {result.examples.slice(0, 10).map((e, i) => (
                    <li key={i}>{e.names.join(' + ')} → keep {e.keep} <span className="text-grey-400">({e.evidence === 'shared_email' ? 'shared email' : 'same firm, same name'})</span></li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function IrPrEdgeRepair() {
  const [runState, setRunState] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const [jobStatus, setJobStatus] = useState<string | null>(null);
  const [result, setResult] = useState<IrPrEdgeRepairResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const run = async (apply: boolean) => {
    if (apply && !window.confirm(
      'Relabel IR/PR firm→client links from their contact lines?\n\n' +
      'Only the label changes (to IR/PR); no link is created and no sighting is counted. Run a dry run first.'
    )) return;
    setRunState('running'); setError(null); setResult(null); setJobStatus(null);
    try {
      setResult(await runIrPrEdgeRepair({ dry_run: !apply }, (s) => setJobStatus(s.status)));
      setRunState('done');
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Request failed');
      setRunState('error');
    }
  };
  return (
    <div className="bg-surface border border-grey-200 rounded-lg p-6 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3 flex-1">
          <div className="mt-0.5 text-grey-500"><Network className="h-5 w-5" /></div>
          <div>
            <h3 className="font-semibold text-grey-900">IR/PR Link Repair</h3>
            <p className="text-sm text-grey-600 mt-1">
              A firm→client link kept the label of the filing that first recorded it. So an IR firm first named in a
              filing&apos;s body stayed &quot;transaction&quot;, even after hundreds of filings listed it as the media or IR
              contact, and fell out of the IR/PR counts (Chapter One showed 1 client of 12). This reads every IR/PR
              contact line, matches it to the filing&apos;s company, and relabels that link as IR/PR.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap sm:ml-4 flex-shrink-0">
          <button onClick={() => run(false)} disabled={runState === 'running'}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-grey-300 text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            <Eye className="h-4 w-4" /> Dry run
          </button>
          <button onClick={() => run(true)} disabled={runState === 'running'}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            <Play className="h-4 w-4" /> Relabel
          </button>
        </div>
      </div>
      {runState !== 'idle' && (
        <div className="mt-4 pt-4 border-t border-grey-100 space-y-2 text-sm">
          {runState === 'running' && <div className="flex items-center gap-2 text-primary-600"><Loader2 className="h-4 w-4 animate-spin" /><span>{jobStatus === 'running' ? 'Running…' : 'Queued as a background job…'}</span></div>}
          {runState === 'error' && <div className="flex items-center gap-2 text-error-700"><XCircle className="h-4 w-4" /><span>{error}</span></div>}
          {runState === 'done' && result && (
            <>
              <div className="text-grey-700">
                {result.lines.toLocaleString()} IR/PR contact lines · {result.filings_mapped.toLocaleString()} of {result.filings.toLocaleString()} filings matched to a company ·{' '}
                {result.pairs.toLocaleString()} firm→client links · <strong>{result.upgraded.toLocaleString()}</strong> {result.dry_run ? 'would be relabelled (nothing changed)' : 'relabelled'}
                {result.pairs_without_edge > 0 && ` · ${result.pairs_without_edge} with no link yet`}
              </div>
              {result.firms.length > 0 && (
                <ul className="text-xs text-grey-600 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-0.5">
                  {result.firms.map((f) => <li key={f.firm}>{f.firm} · {f.upgraded}</li>)}
                </ul>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

const FIRM_REASON_LABEL: Record<string, string> = {
  same_key: 'same name',
  same_key_but_descriptor: 'same name but IR/PR/comms wording — check',
  prefix: 'typo or plural',
  same_group: 'same group',
};

function FirmHygiene() {
  const [runState, setRunState] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const [jobStatus, setJobStatus] = useState<string | null>(null);
  const [result, setResult] = useState<FirmHygieneResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const run = async (apply: boolean) => {
    if (apply && !window.confirm(
      'Merge exact duplicate firm records, queue the rest for review, and end the adviser edges that credit a market operator?\n\n' +
      'Exact = same name, or a record that is only the other one\'s email domain. Everything else waits for approve/reject in the company merge queue. Ended edges are kept with a reason.'
    )) return;
    setRunState('running'); setError(null); setResult(null); setJobStatus(null);
    try {
      setResult(await runFirmHygiene({ dry_run: !apply }, (s) => setJobStatus(s.status)));
      setRunState('done');
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Request failed');
      setRunState('error');
    }
  };
  return (
    <div className="bg-surface border border-grey-200 rounded-lg p-6 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3 flex-1">
          <div className="mt-0.5 text-grey-500"><Building2 className="h-5 w-5" /></div>
          <div>
            <h3 className="font-semibold text-grey-900">Firm Hygiene</h3>
            <p className="text-sm text-grey-600 mt-1">
              Finds adviser and IR/PR firms recorded more than once, such as a real name beside its email domain
              ("NWR Communications" and nwrcommunications.com.au), and queues each duplicate for review in the company
              merge queue. Approving a pair carries its clients, sightings and per-filing links across. It also ends the
              adviser links that credit ASX or ASIC as a company's adviser, because query-letter signatories are not advisers.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap sm:ml-4 flex-shrink-0">
          <button onClick={() => run(false)} disabled={runState === 'running'}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-grey-300 text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            <Eye className="h-4 w-4" /> Dry run
          </button>
          <button onClick={() => run(true)} disabled={runState === 'running'}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            <Play className="h-4 w-4" /> Merge exact · queue rest
          </button>
        </div>
      </div>
      {runState !== 'idle' && (
        <div className="mt-4 pt-4 border-t border-grey-100 space-y-2 text-sm">
          {runState === 'running' && <div className="flex items-center gap-2 text-primary-600"><Loader2 className="h-4 w-4 animate-spin" /><span>{jobStatus === 'running' ? 'Running…' : 'Queued as a background job…'}</span></div>}
          {runState === 'error' && <div className="flex items-center gap-2 text-error-700"><XCircle className="h-4 w-4" /><span>{error}</span></div>}
          {runState === 'done' && result && (
            <>
              <div className="text-grey-700">
                {result.firms_considered.toLocaleString()} firms checked · <strong>{result.duplicates_found}</strong> duplicate records in {result.groups.length} groups
                {result.dry_run ? ' (nothing changed)' : ` · ${result.queued} queued`}
                {result.skipped_already_pending > 0 && ` · ${result.skipped_already_pending} already in the queue`}
                {result.skipped_rejected_before > 0 && ` · ${result.skipped_rejected_before} rejected before (not re-queued)`}
                {result.dry_run
                  ? ` · ${result.would_auto_merge ?? 0} exact (would merge automatically)`
                  : ` · ${result.auto_merged ?? 0} exact merged automatically${result.auto_refused ? ` · ${result.auto_refused} refused (stale)` : ''}`}
              </div>
              <ul className="text-xs text-grey-600 space-y-1">
                {result.groups.map((g) => (
                  <li key={g.survivor.entity_id}>
                    <span className="font-medium text-grey-800">{g.survivor.name}</span>
                    {g.survivor.domain && <span className="text-grey-400"> {g.survivor.domain}</span>}
                    {' '}← {g.duplicates.map((d) => `${d.name}${d.domain && d.domain !== d.name ? ` (${d.domain})` : ''} · ${FIRM_REASON_LABEL[d.reason] ?? d.reason} · ${d.auto_action ? (d.auto_action === 'would_merge' ? 'exact — would merge' : d.auto_action === 'merged' ? 'exact — merged' : d.auto_action) : d.action.replace(/_/g, ' ')}`).join('; ')}
                  </li>
                ))}
              </ul>
              <div className="text-grey-700 pt-1">
                Market-operator adviser links: {result.market_operator_edges.found} found
                {Object.keys(result.market_operator_edges.by_firm).length > 0 &&
                  ` (${Object.entries(result.market_operator_edges.by_firm).map(([k, v]) => `${k} ${v}`).join(', ')})`}
                {result.dry_run ? ' (nothing changed)' : ` · ${result.market_operator_edges.ended} ended`}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function RegateMergeCandidates() {
  const [runState, setRunState] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const [result, setResult] = useState<RegateMergeCandidatesResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const run = async (apply: boolean) => {
    if (apply && !window.confirm(
      'Expire pending merge candidates whose two names are different people?\n\n' +
      'A status change only, recorded with a reason. Run a dry run first to see examples.'
    )) return;
    setRunState('running'); setError(null); setResult(null);
    try {
      setResult(await regateMergeCandidates({ dry_run: !apply }));
      setRunState('done');
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Request failed');
      setRunState('error');
    }
  };
  return (
    <div className="bg-surface border border-grey-200 rounded-lg p-6 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3 flex-1">
          <div className="mt-0.5 text-grey-500"><GitMerge className="h-5 w-5" /></div>
          <div>
            <h3 className="font-semibold text-grey-900">Re-check Merge Candidates by Name</h3>
            <p className="text-sm text-grey-600 mt-1">
              Many pending pairs were queued by a similarity match with no name check, for example two people at the
              same firm. This applies the same-person name check to every pending pair and expires the ones that fail,
              and to every contact-to-person link, ending (not deleting) the ones that fail.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap sm:ml-4 flex-shrink-0">
          <button onClick={() => run(false)} disabled={runState === 'running'}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-grey-300 text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            <Eye className="h-4 w-4" /> Dry run
          </button>
          <button onClick={() => run(true)} disabled={runState === 'running'}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            <Play className="h-4 w-4" /> Expire failures
          </button>
        </div>
      </div>
      {runState !== 'idle' && (
        <div className="mt-4 pt-4 border-t border-grey-100 space-y-2 text-sm">
          {runState === 'running' && <div className="flex items-center gap-2 text-primary-600"><Loader2 className="h-4 w-4 animate-spin" /><span>Checking…</span></div>}
          {runState === 'error' && <div className="flex items-center gap-2 text-error-700"><XCircle className="h-4 w-4" /><span>{error}</span></div>}
          {runState === 'done' && result && (
            <>
              <div className="text-grey-700">
                {result.checked.toLocaleString()} pending checked · <strong>{result.failing.toLocaleString()}</strong> fail the name check
                {result.dry_run ? ' (nothing changed)' : ` · ${result.expired.toLocaleString()} expired`}
              </div>
              {result.examples.length > 0 && (
                <ul className="text-xs text-grey-600 space-y-0.5">
                  {result.examples.map((e, i) => <li key={i}>{e.incoming} ≠ {e.matched}</li>)}
                </ul>
              )}
              {result.links && (
                <>
                  <div className="text-grey-700 pt-1">
                    Contact links: {result.links.checked.toLocaleString()} checked · <strong>{result.links.failing.toLocaleString()}</strong> fail the name check
                    {result.dry_run ? ' (nothing changed)' : ` · ${result.links.ended.toLocaleString()} ended`}
                  </div>
                  {result.links.examples.length > 0 && (
                    <ul className="text-xs text-grey-600 space-y-0.5">
                      {result.links.examples.map((e, i) => <li key={i}>contact {e.contact} ≠ person {e.person}</li>)}
                    </ul>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function KeyRoleSuccession() {
  const [runState, setRunState] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const [mode, setMode] = useState<'dry' | 'dry_codes' | 'apply'>('dry');
  const [codes, setCodes] = useState('');
  const [result, setResult] = useState<KeyRoleSuccessionResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async (m: 'dry' | 'dry_codes' | 'apply') => {
    const codeList = codes.trim().toUpperCase();
    if (m === 'dry_codes' && !codeList) { setError('Enter one or more tickers first.'); setRunState('error'); return; }
    if (m === 'apply' && !window.confirm(
      `Apply succession closures${codeList ? ` for ${codeList}` : ' across every contested seat'}?\n\n` +
      `Writes effective_to + superseded_by on the losing bindings. Run a dry run first and ` +
      `read the closures — the rules never reopen a seat.`
    )) return;
    setRunState('running'); setMode(m); setError(null); setResult(null);
    try {
      const data = await runKeyRoleSuccession({
        dry_run: m !== 'apply',
        codes: codeList || undefined,
      });
      setResult(data);
      setRunState('done');
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Request failed');
      setRunState('error');
    }
  };

  const sampleClosures = (result?.results ?? [])
    .flatMap(r => r.closures.map(c => ({ ...c, entity_id: r.entity_id })))
    .slice(0, 40);

  return (
    <div className="bg-surface border border-grey-200 rounded-lg p-6 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3 flex-1">
          <div className="mt-0.5 text-grey-500"><Users className="h-5 w-5" /></div>
          <div>
            <h3 className="font-semibold text-grey-900">Close Superseded Key Roles (succession)</h3>
            <p className="text-sm text-grey-600 mt-1">
              Person/Org Resolver step 5. Two rules over <code className="text-xs bg-grey-100 px-1 rounded">key_role_bindings</code>,
              keyed on the filing date: a single-holder seat (chair, CEO, MD, CFO, COO, CTO, or a titled seat such as
              "Director of Finance") whose later filings name a different person closes the earlier holder at the successor's
              first filing; a board roster of six or more closes anyone absent from it and not named for 180 days.
              The same logic runs inline after every filing; this replays it. <strong>Dry run</strong> plans every
              contested seat and returns each skip with its reason. <strong>Apply</strong> writes the closures.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap sm:ml-4 flex-shrink-0">
          <input
            value={codes}
            onChange={e => setCodes(e.target.value)}
            placeholder="Tickers, e.g. ATM,RRL"
            className="px-2 py-2 text-sm border border-grey-300 rounded-lg w-40"
          />
          <button onClick={() => run('dry')} disabled={runState === 'running'}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-grey-300 text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            <Eye className="h-4 w-4" /> Dry run (all)
          </button>
          <button onClick={() => run('dry_codes')} disabled={runState === 'running'}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-grey-300 text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            <Eye className="h-4 w-4" /> Dry run (tickers)
          </button>
          <button onClick={() => run('apply')} disabled={runState === 'running'}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            <Play className="h-4 w-4" /> Apply
          </button>
        </div>
      </div>

      {runState !== 'idle' && (
        <div className="mt-4 pt-4 border-t border-grey-100">
          {runState === 'running' && (
            <div className="flex items-center gap-2 text-sm text-primary-600">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>{mode === 'apply' ? 'Applying closures…' : 'Planning closures…'}</span>
            </div>
          )}
          {runState === 'done' && result && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-success-700">
                <CheckCircle className="h-4 w-4" />
                <span>{result.dry_run ? 'Dry run complete' : 'Closures applied'}</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <div className="bg-grey-50 rounded p-2"><div className="text-grey-500">Entities</div><div className="font-mono font-semibold">{result.entities}</div></div>
                <div className="bg-amber-50 rounded p-2"><div className="text-grey-500">Closures planned</div><div className="font-mono font-semibold text-amber-700">{result.closures}</div></div>
                <div className="bg-success-50 rounded p-2"><div className="text-grey-500">Applied</div><div className="font-mono font-semibold text-success-700">{result.applied}</div></div>
                <div className="bg-grey-50 rounded p-2"><div className="text-grey-500">Skips</div><div className="font-mono font-semibold">{result.skips}</div></div>
              </div>
              <div className="text-xs text-grey-600">
                by rule: {Object.entries(result.by_rule).map(([k, v]) => `${k} ${v}`).join(' · ') || '—'}
                {' '}| skips: {Object.entries(result.skip_reasons).map(([k, v]) => `${k} ${v}`).join(' · ') || '—'}
              </div>
              {sampleClosures.length > 0 && (
                <pre className="text-xs bg-grey-50 rounded-md p-3 overflow-x-auto text-grey-800 font-mono max-h-96 overflow-y-auto">
                  {JSON.stringify(sampleClosures, null, 2)}
                </pre>
              )}
            </div>
          )}
          {runState === 'error' && (
            <div className="flex items-center gap-2 text-sm text-red-600">
              <XCircle className="h-4 w-4" />
              <span>{error || 'Failed'}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function KeyRolePersonBackfill() {
  const [runState, setRunState] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const [mode, setMode] = useState<'dry' | 'small' | 'large'>('dry');
  const [result, setResult] = useState<BackfillKeyRolePersonResult | null>(null);
  const [progress, setProgress] = useState<KeyRolePersonBackfillProgress | null>(null);
  const [polling, setPolling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchProgress = async () => {
      try {
        const p = await getKeyRolePersonBackfillProgress();
        if (!cancelled) setProgress(p);
      } catch { /* best-effort */ }
    };
    fetchProgress();
    if (polling) {
      const id = setInterval(fetchProgress, 5000);
      return () => { cancelled = true; clearInterval(id); };
    }
    return () => { cancelled = true; };
  }, [polling]);

  const run = async (m: 'dry' | 'small' | 'large') => {
    const params =
      m === 'dry'   ? { dry_run: true,  limit: 10 } :
      m === 'small' ? { dry_run: false, limit: 50 } :
                      { dry_run: false, limit: 5000 };
    if (m === 'large' && !window.confirm(
      `Run a 5000-row backfill batch?\n\n` +
      `This calls Cynapse /api/entities/persons sequentially for each ` +
      `unbound key_role_binding. Expect ~15-30 minutes wall-clock; the ` +
      `request runs server-side so the browser tab can close without ` +
      `aborting. Progress panel above shows live counts.`
    )) return;
    setRunState('running');
    setMode(m);
    setError(null);
    setResult(null);
    setPolling(true);
    try {
      const data = await backfillKeyRolePersonBindings(params);
      setResult(data);
      setRunState('done');
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Request failed');
      setRunState('error');
    }
  };

  return (
    <div className="bg-surface border border-grey-200 rounded-lg p-6 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3 flex-1">
          <div className="mt-0.5 text-grey-500"><UserCheck className="h-5 w-5" /></div>
          <div>
            <h3 className="font-semibold text-grey-900">Backfill Key Role Person Bindings</h3>
            <p className="text-sm text-grey-600 mt-1">
              Populates <code className="text-xs bg-grey-100 px-1 rounded">key_role_bindings.person_entity_id</code> for unbound rows.
              Calls Cynapse <code className="text-xs bg-grey-100 px-1 rounded">/api/entities/persons</code> (FR mig 147 — canonical editorial Person identity).
              Resolves person_name + employer (from the key role's company entity) to a Cynapse <code className="text-xs bg-grey-100 px-1 rounded">global_entities.entity_type='person'</code> row.
              <strong> Dry Run</strong> previews 10 rows. <strong>Execute (50)</strong> runs a synchronous small batch.
              <strong> Run Large Batch (5000)</strong> runs server-side; safe to close the tab while it drains.
              Progress polls every 5s.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap sm:ml-4 flex-shrink-0">
          <button
            onClick={() => run('dry')}
            disabled={runState === 'running'}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-grey-300 text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Eye className="h-4 w-4" /> Dry Run
          </button>
          <button
            onClick={() => run('small')}
            disabled={runState === 'running'}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-grey-300 text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Play className="h-4 w-4" /> Execute (50)
          </button>
          <button
            onClick={() => run('large')}
            disabled={runState === 'running'}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Play className="h-4 w-4" /> Run Large Batch (5000)
          </button>
        </div>
      </div>

      {progress && (
        <div className="mt-4 pt-4 border-t border-grey-100">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-wide text-grey-500 font-semibold">Pipeline progress</div>
            <button
              onClick={() => setPolling(p => !p)}
              className={`text-xs px-2 py-1 rounded border transition-colors ${
                polling
                  ? 'border-primary-300 bg-primary-50 text-primary-700'
                  : 'border-grey-300 text-grey-700 hover:bg-grey-50'
              }`}
            >
              {polling ? (
                <span className="flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Polling</span>
              ) : 'Refresh on change'}
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 text-xs">
            <div className="bg-grey-50 rounded p-2"><div className="text-grey-500">Total</div><div className="font-mono font-semibold">{progress.total}</div></div>
            <div className="bg-success-50 rounded p-2"><div className="text-grey-500">Bound</div><div className="font-mono font-semibold text-success-700">{progress.bound}</div></div>
            <div className="bg-amber-50 rounded p-2"><div className="text-grey-500">Unbound (total)</div><div className="font-mono font-semibold text-amber-700">{progress.unbound}</div></div>
            <div className="bg-blue-50 rounded p-2"><div className="text-grey-500">↳ resolved</div><div className="font-mono font-semibold text-blue-700">{progress.unbound_resolved}</div></div>
            <div className="bg-grey-50 rounded p-2"><div className="text-grey-500">↳ pending</div><div className="font-mono font-semibold">{progress.unbound_pending}</div></div>
            <div className="bg-grey-50 rounded p-2"><div className="text-grey-500">↳ unresolvable</div><div className="font-mono font-semibold">{progress.unbound_unresolvable}</div></div>
          </div>
        </div>
      )}

      {runState !== 'idle' && (
        <div className="mt-4 pt-4 border-t border-grey-100">
          {runState === 'running' && (
            <div className="flex items-center gap-2 text-sm text-primary-600">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>
                {mode === 'dry' ? 'Running dry run (10 rows)…'
                  : mode === 'small' ? 'Running batch (50 rows synchronous)…'
                  : 'Running large batch (5000 rows server-side)…'}
              </span>
            </div>
          )}
          {runState === 'done' && result && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-success-700">
                <CheckCircle className="h-4 w-4" />
                <span>{mode === 'dry' ? 'Dry run complete' : 'Batch complete'}</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 text-xs">
                <div className="bg-grey-50 rounded p-2"><div className="text-grey-500">Processed</div><div className="font-mono font-semibold">{result.stats.processed}</div></div>
                <div className="bg-success-50 rounded p-2"><div className="text-grey-500">Bound</div><div className="font-mono font-semibold text-success-700">{result.stats.bound}</div></div>
                <div className="bg-amber-50 rounded p-2"><div className="text-grey-500">No match</div><div className="font-mono font-semibold text-amber-700">{result.stats.no_match}</div></div>
                <div className="bg-red-50 rounded p-2"><div className="text-grey-500">Error</div><div className="font-mono font-semibold text-red-700">{result.stats.error}</div></div>
                <div className="bg-grey-50 rounded p-2"><div className="text-grey-500">Skipped</div><div className="font-mono font-semibold">{result.stats.skipped}</div></div>
              </div>
              {result.error && (
                <div className="text-xs text-red-700">Service error: {result.error}</div>
              )}
              <pre className="text-xs bg-grey-50 rounded-md p-3 overflow-x-auto text-grey-800 font-mono max-h-96 overflow-y-auto">
                {JSON.stringify(result.examples, null, 2)}
              </pre>
            </div>
          )}
          {runState === 'error' && (
            <div className="flex items-center gap-2 text-sm text-red-600">
              <XCircle className="h-4 w-4" />
              <span>{error || 'Failed'}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}



function EntityDriftSweep() {
  const [runState, setRunState] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const [mode, setMode] = useState<'dry' | 'execute'>('dry');
  const [result, setResult] = useState<DriftSweepResult | null>(null);
  const [progress, setProgress] = useState<DriftSweepProgress | null>(null);
  const [polling, setPolling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchProgress = async () => {
      try {
        const p = await getDriftSweepProgress();
        if (!cancelled) setProgress(p);
      } catch { /* best-effort */ }
    };
    fetchProgress();
    if (polling) {
      const id = setInterval(fetchProgress, 5000);
      return () => { cancelled = true; clearInterval(id); };
    }
    return () => { cancelled = true; };
  }, [polling]);

  const run = async (m: 'dry' | 'execute') => {
    if (m === 'execute' && !window.confirm(
      `Run the full drift sweep?\n\n` +
      `Walks entity_local_state for display_name divergence from the latest ` +
      `announcement.company_name. Each divergence is queued into Cynapse's ` +
      `attribute_change_candidates for operator review via the ` +
      `MergeCandidatesPage Attribute Changes tab.\n\n` +
      `Idempotent — re-queues are blocked by the partial unique index. Safe ` +
      `to re-run after operator triage.`
    )) return;
    setRunState('running');
    setMode(m);
    setError(null);
    setResult(null);
    setPolling(true);
    try {
      const data = await triggerDriftSweep(m === 'dry' ? { dry_run: true } : { dry_run: false });
      setResult(data);
      setRunState('done');
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Request failed');
      setRunState('error');
    }
  };

  return (
    <div className="bg-surface border border-grey-200 rounded-lg p-6 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3 flex-1">
          <div className="mt-0.5 text-grey-500"><RefreshCw className="h-5 w-5" /></div>
          <div>
            <h3 className="font-semibold text-grey-900">Entity Drift Sweep</h3>
            <p className="text-sm text-grey-600 mt-1">
              Surfaces MM8-class entities: rows where <code className="text-xs bg-grey-100 px-1 rounded">entity_local_state.display_name</code> diverges from the most recent <code className="text-xs bg-grey-100 px-1 rounded">announcement.company_name</code> for that entity.
              Each divergence is queued into Cynapse <code className="text-xs bg-grey-100 px-1 rounded">attribute_change_candidates</code> for operator review on the
              <Link to="/entities/merge-review?tab=attribute-changes" className="text-primary-600 hover:text-primary-800 underline mx-1">Attribute Changes</Link>
              tab.
              <strong> Dry Run</strong> previews the population size without writing.
              <strong> Execute</strong> queues all divergences. Idempotent — duplicate proposals don't re-queue.
              FR plan Phase 2 — Bucket B Gap 4.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap sm:ml-4 flex-shrink-0">
          <button
            onClick={() => run('dry')}
            disabled={runState === 'running'}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-grey-300 text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Eye className="h-4 w-4" /> Dry Run
          </button>
          <button
            onClick={() => run('execute')}
            disabled={runState === 'running'}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Play className="h-4 w-4" /> Execute
          </button>
        </div>
      </div>

      {progress && (
        <div className="mt-4 pt-4 border-t border-grey-100">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-wide text-grey-500 font-semibold">Drifted population</div>
            <button
              onClick={() => setPolling(p => !p)}
              className={`text-xs px-2 py-1 rounded border transition-colors ${
                polling
                  ? 'border-primary-300 bg-primary-50 text-primary-700'
                  : 'border-grey-300 text-grey-700 hover:bg-grey-50'
              }`}
            >
              {polling ? (
                <span className="flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Polling</span>
              ) : 'Refresh on change'}
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
            <div className="bg-amber-50 rounded p-2"><div className="text-grey-500">Total drifted</div><div className="font-mono font-semibold text-amber-700">{progress.drifted}</div></div>
            <div className="bg-success-50 rounded p-2"><div className="text-grey-500">↳ with Cynapse id (queueable)</div><div className="font-mono font-semibold text-success-700">{progress.drifted_with_cynapse_id}</div></div>
            <div className="bg-grey-50 rounded p-2"><div className="text-grey-500">↳ no Cynapse id (bind first)</div><div className="font-mono font-semibold text-grey-700">{progress.drifted_no_cynapse_id}</div></div>
          </div>
        </div>
      )}

      {runState !== 'idle' && (
        <div className="mt-4 pt-4 border-t border-grey-100">
          {runState === 'running' && (
            <div className="flex items-center gap-2 text-sm text-primary-600">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>{mode === 'dry' ? 'Scanning for drift (no writes)…' : 'Scanning + queuing divergences to Cynapse…'}</span>
            </div>
          )}
          {runState === 'done' && result && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-success-700">
                <CheckCircle className="h-4 w-4" />
                <span>{mode === 'dry' ? 'Dry run complete' : 'Sweep complete'}</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 text-xs">
                <div className="bg-grey-50 rounded p-2"><div className="text-grey-500">Scanned</div><div className="font-mono font-semibold">{result.stats.scanned}</div></div>
                <div className="bg-amber-50 rounded p-2"><div className="text-grey-500">Drifted</div><div className="font-mono font-semibold text-amber-700">{result.stats.drifted}</div></div>
                <div className="bg-success-50 rounded p-2"><div className="text-grey-500">Queued</div><div className="font-mono font-semibold text-success-700">{result.stats.queued}</div></div>
                <div className="bg-blue-50 rounded p-2"><div className="text-grey-500">Duplicate</div><div className="font-mono font-semibold text-blue-700">{result.stats.duplicate}</div></div>
                <div className="bg-red-50 rounded p-2"><div className="text-grey-500">Error</div><div className="font-mono font-semibold text-red-700">{result.stats.error}</div></div>
                <div className="bg-grey-50 rounded p-2"><div className="text-grey-500">No Cynapse id</div><div className="font-mono font-semibold">{result.stats.skipped_no_cynapse_id}</div></div>
              </div>
              {result.error && (
                <div className="text-xs text-red-700">Service error: {result.error}</div>
              )}
              <pre className="text-xs bg-grey-50 rounded-md p-3 overflow-x-auto text-grey-800 font-mono max-h-96 overflow-y-auto">
                {JSON.stringify(result.examples, null, 2)}
              </pre>
            </div>
          )}
          {runState === 'error' && (
            <div className="flex items-center gap-2 text-sm text-red-600">
              <XCircle className="h-4 w-4" />
              <span>{error || 'Failed'}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function CanonicalNormalisation() {
  const [runState, setRunState] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const [mode, setMode] = useState<'dry' | 'execute'>('dry');
  const [result, setResult] = useState<CanonicalNormalisationResult | null>(null);
  const [progress, setProgress] = useState<CanonicalNormalisationProgress | null>(null);
  const [polling, setPolling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchProgress = async () => {
      try {
        const p = await getCanonicalNormalisationProgress();
        if (!cancelled) setProgress(p);
      } catch { /* best-effort */ }
    };
    fetchProgress();
    if (polling) {
      const id = setInterval(fetchProgress, 10000);
      return () => { cancelled = true; clearInterval(id); };
    }
    return () => { cancelled = true; };
  }, [polling]);

  const run = async (m: 'dry' | 'execute') => {
    if (m === 'execute' && !window.confirm(
      `Run canonical-name normalisation across all entities?\n\n` +
      `For every entity_local_state row whose display_name ends in a strippable ` +
      `legal-form suffix (Pty Ltd / Limited / Plc / Inc / etc, jurisdiction-aware), ` +
      `this:\n` +
      `  1. Records the prior canonical_name in entity_aliases (alias_kind='legal_name')\n` +
      `  2. Updates global_entities.canonical_name (Cynapse) to the stripped form\n` +
      `  3. Refreshes the name embedding inline\n` +
      `  4. Updates entity_local_state.display_name (Assayer) to match\n\n` +
      `Server-side, idempotent. Safe to close the tab while it runs.`
    )) return;
    setRunState('running');
    setMode(m);
    setError(null);
    setResult(null);
    setPolling(true);
    try {
      const data = await triggerCanonicalNormalisation(m === 'dry' ? { dry_run: true } : { dry_run: false });
      setResult(data);
      setRunState('done');
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Request failed');
      setRunState('error');
    }
  };

  return (
    <div className="bg-surface border border-grey-200 rounded-lg p-6 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3 flex-1">
          <div className="mt-0.5 text-grey-500"><Sparkles className="h-5 w-5" /></div>
          <div>
            <h3 className="font-semibold text-grey-900">Canonical Name Normalisation (Step B)</h3>
            <p className="text-sm text-grey-600 mt-1">
              Strips legal-form suffixes (Pty Ltd / Limited / Plc / Inc / etc, jurisdiction-aware) from
              entity <code className="text-xs bg-grey-100 px-1 rounded">display_name</code> values.
              Records the original as a <code className="text-xs bg-grey-100 px-1 rounded">legal_name</code> alias.
              Editorial canonical becomes the suffix-free form ("Wesfarmers" not "Wesfarmers Limited").
              <strong> Dry Run</strong> previews the count + first 20 examples.
              <strong> Execute</strong> applies across all entities — idempotent, safe to close tab.
              See <code className="text-xs bg-grey-100 px-1 rounded">project_entity_name_layers</code> doctrine.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap sm:ml-4 flex-shrink-0">
          <button
            onClick={() => run('dry')}
            disabled={runState === 'running'}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-grey-300 text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Eye className="h-4 w-4" /> Dry Run
          </button>
          <button
            onClick={() => run('execute')}
            disabled={runState === 'running'}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Play className="h-4 w-4" /> Execute
          </button>
        </div>
      </div>

      {progress && (
        <div className="mt-4 pt-4 border-t border-grey-100">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-wide text-grey-500 font-semibold">Population needing cleanup</div>
            <button
              onClick={() => setPolling(p => !p)}
              className={`text-xs px-2 py-1 rounded border transition-colors ${
                polling
                  ? 'border-primary-300 bg-primary-50 text-primary-700'
                  : 'border-grey-300 text-grey-700 hover:bg-grey-50'
              }`}
            >
              {polling ? (
                <span className="flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Polling</span>
              ) : 'Refresh on change'}
            </button>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 text-xs">
            <div className="bg-grey-50 rounded p-2"><div className="text-grey-500">Total entities</div><div className="font-mono font-semibold">{progress.total}</div></div>
            <div className="bg-amber-50 rounded p-2"><div className="text-grey-500">Need cleanup</div><div className="font-mono font-semibold text-amber-700">{progress.needs_cleanup}</div></div>
            <div className="bg-success-50 rounded p-2"><div className="text-grey-500">↳ with Cynapse id</div><div className="font-mono font-semibold text-success-700">{progress.needs_cleanup_with_cynapse_id}</div></div>
            <div className="bg-grey-50 rounded p-2"><div className="text-grey-500">↳ no Cynapse id (bind first)</div><div className="font-mono font-semibold text-grey-700">{progress.needs_cleanup_no_cynapse_id}</div></div>
          </div>
        </div>
      )}

      {runState !== 'idle' && (
        <div className="mt-4 pt-4 border-t border-grey-100">
          {runState === 'running' && (
            <div className="flex items-center gap-2 text-sm text-primary-600">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>{mode === 'dry' ? 'Scanning for strippable names (no writes)…' : 'Applying canonical normalisation server-side…'}</span>
            </div>
          )}
          {runState === 'done' && result && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-success-700">
                <CheckCircle className="h-4 w-4" />
                <span>{mode === 'dry' ? 'Dry run complete' : 'Normalisation complete'}</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 text-xs">
                <div className="bg-grey-50 rounded p-2"><div className="text-grey-500">Scanned</div><div className="font-mono font-semibold">{result.stats.scanned}</div></div>
                <div className="bg-amber-50 rounded p-2"><div className="text-grey-500">Strippable</div><div className="font-mono font-semibold text-amber-700">{result.stats.drifted}</div></div>
                <div className="bg-success-50 rounded p-2"><div className="text-grey-500">Applied</div><div className="font-mono font-semibold text-success-700">{result.stats.applied}</div></div>
                <div className="bg-blue-50 rounded p-2"><div className="text-grey-500">Duplicate</div><div className="font-mono font-semibold text-blue-700">{result.stats.duplicate}</div></div>
                <div className="bg-red-50 rounded p-2"><div className="text-grey-500">Error</div><div className="font-mono font-semibold text-red-700">{result.stats.error}</div></div>
                <div className="bg-grey-50 rounded p-2"><div className="text-grey-500">No Cynapse id</div><div className="font-mono font-semibold">{result.stats.skipped_no_cynapse_id}</div></div>
                <div className="bg-grey-50 rounded p-2"><div className="text-grey-500">No change</div><div className="font-mono font-semibold">{result.stats.skipped_no_change}</div></div>
              </div>
              {result.error && (
                <div className="text-xs text-red-700">Service error: {result.error}</div>
              )}
              <pre className="text-xs bg-grey-50 rounded-md p-3 overflow-x-auto text-grey-800 font-mono max-h-96 overflow-y-auto">
                {JSON.stringify(result.examples, null, 2)}
              </pre>
            </div>
          )}
          {runState === 'error' && (
            <div className="flex items-center gap-2 text-sm text-red-600">
              <XCircle className="h-4 w-4" />
              <span>{error || 'Failed'}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function ExternalCodesBackfill() {
  const [runState, setRunState] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const [mode, setMode] = useState<'dry' | 'execute'>('dry');
  const [result, setResult] = useState<ExternalCodesBackfillResult | null>(null);
  const [progress, setProgress] = useState<ExternalCodesBackfillProgress | null>(null);
  const [polling, setPolling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchProgress = async () => {
      try {
        const p = await getExternalCodesBackfillProgress();
        if (!cancelled) setProgress(p);
      } catch { /* best-effort */ }
    };
    fetchProgress();
    if (polling) {
      const id = setInterval(fetchProgress, 10000);
      return () => { cancelled = true; clearInterval(id); };
    }
    return () => { cancelled = true; };
  }, [polling]);

  const run = async (m: 'dry' | 'execute') => {
    if (m === 'execute' && !window.confirm(
      `Run entity_external_codes backfill across all ASX-listed entities?\n\n` +
      `For every entity_local_state row of type asx_company / etf / managed_fund / ` +
      `reit / lic with a ticker (canonical_identifier) AND a Cynapse binding, ` +
      `this writes an entity_external_codes row on Cynapse with ` +
      `code_system='asx_ticker' and code_value=ticker.\n\n` +
      `Idempotent (ON CONFLICT DO NOTHING). Cuts off the name-fallback path ` +
      `in the Cynapse resolver that enabled the MM8/MRT contamination case. ` +
      `Pre-requisite for the Entity Mismatch Audit.`
    )) return;
    setRunState('running');
    setMode(m);
    setError(null);
    setResult(null);
    setPolling(true);
    try {
      const data = await triggerExternalCodesBackfill(m === 'dry' ? { dry_run: true } : { dry_run: false });
      setResult(data);
      setRunState('done');
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Request failed');
      setRunState('error');
    }
  };

  return (
    <div className="bg-surface border border-grey-200 rounded-lg p-6 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3 flex-1">
          <div className="mt-0.5 text-grey-500"><Sparkles className="h-5 w-5" /></div>
          <div>
            <h3 className="font-semibold text-grey-900">External Codes Backfill (asx_ticker)</h3>
            <p className="text-sm text-grey-600 mt-1">
              Populates Cynapse's <code className="text-xs bg-grey-100 px-1 rounded">entity_external_codes</code> from
              Assayer's ticker store for ASX-listed entity types. Anchors resolver lookups by code rather than name —
              cuts off the name-fallback path that produced the MM8/MRT contamination case.
              <strong> Dry Run</strong> previews counts + first 20 examples.
              <strong> Execute</strong> writes one row per (entity, ticker) — idempotent, safe to re-run.
              Pre-condition for the Entity Mismatch Audit.
              See <code className="text-xs bg-grey-100 px-1 rounded">project_entity_external_codes_gap</code>.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap sm:ml-4 flex-shrink-0">
          <button
            onClick={() => run('dry')}
            disabled={runState === 'running'}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-grey-300 text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Eye className="h-4 w-4" /> Dry Run
          </button>
          <button
            onClick={() => run('execute')}
            disabled={runState === 'running'}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Play className="h-4 w-4" /> Execute
          </button>
        </div>
      </div>

      {progress && (
        <div className="mt-4 pt-4 border-t border-grey-100">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-wide text-grey-500 font-semibold">ASX-listed entity coverage</div>
            <button
              onClick={() => setPolling(p => !p)}
              className={`text-xs px-2 py-1 rounded border transition-colors ${
                polling
                  ? 'border-primary-300 bg-primary-50 text-primary-700'
                  : 'border-grey-300 text-grey-700 hover:bg-grey-50'
              }`}
            >
              {polling ? (
                <span className="flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Polling</span>
              ) : 'Refresh on change'}
            </button>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 text-xs">
            <div className="bg-grey-50 rounded p-2"><div className="text-grey-500">Total ASX-listed</div><div className="font-mono font-semibold">{progress.total}</div></div>
            <div className="bg-grey-50 rounded p-2"><div className="text-grey-500">Has ticker</div><div className="font-mono font-semibold">{progress.has_ticker}</div></div>
            <div className="bg-grey-50 rounded p-2"><div className="text-grey-500">Has Cynapse binding</div><div className="font-mono font-semibold">{progress.has_cynapse}</div></div>
            <div className="bg-success-50 rounded p-2"><div className="text-grey-500">Backfillable</div><div className="font-mono font-semibold text-success-700">{progress.backfillable}</div></div>
          </div>
        </div>
      )}

      {runState !== 'idle' && (
        <div className="mt-4 pt-4 border-t border-grey-100">
          {runState === 'running' && (
            <div className="flex items-center gap-2 text-sm text-primary-600">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>{mode === 'dry' ? 'Scanning ASX-listed entities (no writes)…' : 'Writing entity_external_codes rows to Cynapse…'}</span>
            </div>
          )}
          {runState === 'done' && result && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-success-700">
                <CheckCircle className="h-4 w-4" />
                <span>{mode === 'dry' ? 'Dry run complete' : 'Backfill complete'}</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 text-xs">
                <div className="bg-grey-50 rounded p-2"><div className="text-grey-500">Scanned</div><div className="font-mono font-semibold">{result.stats.scanned}</div></div>
                <div className="bg-amber-50 rounded p-2"><div className="text-grey-500">Candidates</div><div className="font-mono font-semibold text-amber-700">{result.stats.candidates}</div></div>
                <div className="bg-success-50 rounded p-2"><div className="text-grey-500">Created</div><div className="font-mono font-semibold text-success-700">{result.stats.created}</div></div>
                <div className="bg-blue-50 rounded p-2"><div className="text-grey-500">Already present</div><div className="font-mono font-semibold text-blue-700">{result.stats.already_present}</div></div>
                <div className="bg-red-50 rounded p-2"><div className="text-grey-500">Error</div><div className="font-mono font-semibold text-red-700">{result.stats.error}</div></div>
                <div className="bg-grey-50 rounded p-2"><div className="text-grey-500">No Cynapse id</div><div className="font-mono font-semibold">{result.stats.skipped_no_cynapse_id}</div></div>
                <div className="bg-grey-50 rounded p-2"><div className="text-grey-500">No ticker</div><div className="font-mono font-semibold">{result.stats.skipped_no_ticker}</div></div>
              </div>
              {result.error && (
                <div className="text-xs text-red-700">Service error: {result.error}</div>
              )}
              <pre className="text-xs bg-grey-50 rounded-md p-3 overflow-x-auto text-grey-800 font-mono max-h-96 overflow-y-auto">
                {JSON.stringify(result.examples, null, 2)}
              </pre>
            </div>
          )}
          {runState === 'error' && (
            <div className="flex items-center gap-2 text-sm text-red-600">
              <XCircle className="h-4 w-4" />
              <span>{error || 'Failed'}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function EntityMismatchAudit() {
  const [runState, setRunState] = useState<'idle' | 'submitting' | 'running' | 'done' | 'error'>('idle');
  const [runId, setRunId] = useState<string | null>(null);
  const [result, setResult] = useState<EntityMismatchAuditResult | null>(null);
  const [progress, setProgress] = useState<EntityMismatchAuditProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'b' | 'a'>('b');
  const [withExternal, setWithExternal] = useState(false);
  const runIdRef = useRef<string | null>(null);

  useEffect(() => { runIdRef.current = runId; }, [runId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const p = await getEntityMismatchAuditProgress();
        if (!cancelled) setProgress(p);
      } catch { /* best-effort */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Subscribe to the shared assayerai:tools WS channel; filter on our run_id.
  // Subscribed through `onEvent` — useWebSocket returns `{ connected }` and
  // nothing else. This card previously destructured a `lastMessage` that has
  // never existed, so the whole handler was dead and the poll fallback below
  // was doing all the work. Registered once, so it reads live values through
  // refs only.
  //
  // `results` is OPTIONAL on the emitted payload (_emit_tools_status only
  // includes it when the caller passes one), so completion falls back to the
  // run record rather than blanking a real result.
  const handleToolStatus = useCallback((payload: any) => {
    if (!payload || payload.tool_name !== 'mismatch_audit') return;
    if (!runIdRef.current || payload.run_id !== runIdRef.current) return;
    if (payload.status === 'completed') {
      if (payload.results) {
        setResult(payload.results);
        setRunState('done');
      } else {
        getToolRunStatus(payload.run_id)
          .then((s) => { setResult((s.result as EntityMismatchAuditResult) ?? null); setRunState('done'); })
          .catch(() => setRunState('done'));
      }
    } else if (payload.status === 'failed') {
      setError(payload.error || 'Job failed');
      setRunState('error');
    } else if (payload.status === 'running') {
      setRunState('running');
    }
  }, []);

  useWebSocket({
    channels: ['assayerai:tools'],
    autoInvalidate: false,
    onEvent: { tool_status: handleToolStatus },
  });

  // Poll fallback in case the WS event is missed.
  useEffect(() => {
    if (runState !== 'submitting' && runState !== 'running') return;
    if (!runId) return;
    let cancelled = false;
    (async () => {
      for (let i = 0; i < 60; i++) {
        await new Promise((r) => setTimeout(r, 5000));
        if (cancelled) return;
        try {
          const s: any = await getToolRunStatus(runId);
          if (s.status === 'completed') {
            setResult(s.result || null);
            setRunState('done');
            return;
          }
          if (s.status === 'failed') {
            setError(s.error || 'Job failed');
            setRunState('error');
            return;
          }
        } catch { /* keep polling */ }
      }
    })();
    return () => { cancelled = true; };
  }, [runState, runId]);

  const run = async () => {
    setRunState('submitting');
    setError(null);
    setResult(null);
    setRunId(null);
    try {
      const sub = await submitEntityMismatchAudit({
        limit: 50,
        with_external_evidence: withExternal,
      });
      setRunId(sub.run_id);
      setRunState('running');
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Request failed');
      setRunState('error');
    }
  };

  return (
    <div className="bg-surface border border-grey-200 rounded-lg p-6 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3 flex-1">
          <div className="mt-0.5 text-grey-500"><Sparkles className="h-5 w-5" /></div>
          <div>
            <h3 className="font-semibold text-grey-900">Entity Mismatch Audit</h3>
            <p className="text-sm text-grey-600 mt-1">
              Scans bound announcements for two failure shapes:
              <br/>
              <strong>Bucket B (wrong canonical):</strong> entity's stored name disagrees with the majority of its announcements.
              Fix via the entity edit form — change <code className="text-xs bg-grey-100 px-1 rounded">display_name</code> directly,
              <strong> do NOT</strong> use the Attribute Changes approve action (that would write the contamination as a rebrand alias).
              <br/>
              <strong>Bucket A (wrong binding):</strong> a small minority of an entity's announcements fail the name-similarity guard —
              likely misbound. Fix via SQL <code className="text-xs bg-grey-100 px-1 rounded">UPDATE announcements SET entity_id=...</code>.
              <br/>
              Read-only scan — no writes. Re-runnable. See <code className="text-xs bg-grey-100 px-1 rounded">feedback_contamination_vs_rebrand_fork</code>.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 ml-4 flex-shrink-0">
          <label className="flex items-center gap-1.5 text-xs text-grey-700 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={withExternal}
              onChange={(e) => setWithExternal(e.target.checked)}
              disabled={runState === 'running'}
              className="rounded border-grey-300"
            />
            Fetch external evidence (marketindex)
          </label>
          <button
            onClick={run}
            disabled={runState === 'submitting' || runState === 'running'}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Play className="h-4 w-4" /> Run Scan
          </button>
        </div>
      </div>

      {progress && (
        <div className="mt-4 pt-4 border-t border-grey-100">
          <div className="text-xs uppercase tracking-wide text-grey-500 font-semibold mb-2">Population</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
            <div className="bg-grey-50 rounded p-2"><div className="text-grey-500">Bound entities</div><div className="font-mono font-semibold">{progress.entities_with_announcements}</div></div>
            <div className="bg-grey-50 rounded p-2"><div className="text-grey-500">Bound announcements</div><div className="font-mono font-semibold">{progress.bound_announcements}</div></div>
            <div className="bg-amber-50 rounded p-2"><div className="text-grey-500">Raw name mismatch</div><div className="font-mono font-semibold text-amber-700">{progress.name_mismatch_raw}</div></div>
          </div>
          <p className="text-xs text-grey-400 mt-1">
            "Raw mismatch" counts case-sensitive name differences without the similarity guard — most are suffix / case noise.
          </p>
        </div>
      )}

      {runState !== 'idle' && (
        <div className="mt-4 pt-4 border-t border-grey-100">
          {(runState === 'submitting' || runState === 'running') && (
            <div className="flex items-center gap-2 text-sm text-primary-600">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>
                {runState === 'submitting' ? 'Submitting…' : 'Scanning server-side — safe to navigate away'}
                {withExternal && runState === 'running' && ' (external lookup ~60s on first run)'}
              </span>
              {runId && <span className="text-xs text-grey-400 font-mono ml-2">{runId.slice(0, 8)}…</span>}
            </div>
          )}
          {runState === 'done' && result && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-success-700">
                <CheckCircle className="h-4 w-4" />
                <span>Scan complete</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 text-xs">
                <div className="bg-grey-50 rounded p-2"><div className="text-grey-500">Entities scanned</div><div className="font-mono font-semibold">{result.stats.entities_scanned}</div></div>
                <div className="bg-grey-50 rounded p-2"><div className="text-grey-500">Anns scanned</div><div className="font-mono font-semibold">{result.stats.announcements_scanned}</div></div>
                <div className="bg-red-50 rounded p-2"><div className="text-grey-500">Bucket B entities</div><div className="font-mono font-semibold text-red-700">{result.stats.bucket_b_canonical_correction_candidates}</div></div>
                <div className="bg-amber-50 rounded p-2"><div className="text-grey-500">Bucket A anns</div><div className="font-mono font-semibold text-amber-700">{result.stats.bucket_a_misbinding_candidates}</div></div>
                <div className="bg-amber-50 rounded p-2"><div className="text-grey-500">Bucket A entities</div><div className="font-mono font-semibold text-amber-700">{result.stats.bucket_a_affected_entities}</div></div>
              </div>

              <div className="flex items-center gap-2 border-b border-grey-200">
                <button
                  onClick={() => setActiveTab('b')}
                  className={`text-xs px-3 py-2 font-medium border-b-2 transition-colors ${
                    activeTab === 'b'
                      ? 'border-red-600 text-red-700'
                      : 'border-transparent text-grey-600 hover:text-grey-900'
                  }`}
                >
                  Bucket B — Wrong canonical ({result.stats.bucket_b_canonical_correction_candidates})
                </button>
                <button
                  onClick={() => setActiveTab('a')}
                  className={`text-xs px-3 py-2 font-medium border-b-2 transition-colors ${
                    activeTab === 'a'
                      ? 'border-amber-600 text-amber-700'
                      : 'border-transparent text-grey-600 hover:text-grey-900'
                  }`}
                >
                  Bucket A — Wrong binding ({result.stats.bucket_a_misbinding_candidates} in {result.stats.bucket_a_affected_entities} entities)
                </button>
              </div>

              {activeTab === 'b' && (
                <div className="space-y-2">
                  {result.bucket_b_canonical_correction_examples.length === 0 ? (
                    <p className="text-xs text-grey-500">No Bucket B candidates — no entities found where stored canonical disagrees with announcement-majority name.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-grey-50">
                          <tr>
                            <th className="text-left px-2 py-1 font-semibold text-grey-700">Ticker</th>
                            <th className="text-left px-2 py-1 font-semibold text-grey-700">Stored canonical (contaminated)</th>
                            <th className="text-left px-2 py-1 font-semibold text-grey-700">Majority name</th>
                            <th className="text-right px-2 py-1 font-semibold text-grey-700">Majority</th>
                            <th className="text-right px-2 py-1 font-semibold text-grey-700">Total anns</th>
                            <th className="text-left px-2 py-1 font-semibold text-grey-700">Top distribution</th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.bucket_b_canonical_correction_examples.map((c) => (
                            <tr key={c.entity_id} className="border-b border-grey-100">
                              <td className="px-2 py-1 font-mono text-grey-700">{c.ticker || '—'}</td>
                              <td className="px-2 py-1 text-red-700">{c.stored_canonical_name}</td>
                              <td className="px-2 py-1 text-success-700 font-medium">{c.majority_name}</td>
                              <td className="px-2 py-1 text-right font-mono">{c.majority_count}</td>
                              <td className="px-2 py-1 text-right font-mono text-grey-500">{c.total_announcements}</td>
                              <td className="px-2 py-1 text-grey-600">
                                {c.top_name_distribution.map((d, i) => (
                                  <span key={i} className="inline-block mr-2">{d.name} <span className="text-grey-400">({d.count})</span></span>
                                ))}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'a' && (
                <div className="space-y-2">
                  {result.bucket_a_misbinding_examples.length === 0 ? (
                    <p className="text-xs text-grey-500">No Bucket A candidates — no minority-failing announcements found.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-grey-50">
                          <tr>
                            <th className="text-left px-2 py-1 font-semibold text-grey-700">Announcement</th>
                            <th className="text-left px-2 py-1 font-semibold text-grey-700">Date</th>
                            <th className="text-left px-2 py-1 font-semibold text-grey-700">Ann company_name</th>
                            <th className="text-left px-2 py-1 font-semibold text-grey-700">Bound ticker</th>
                            <th className="text-left px-2 py-1 font-semibold text-grey-700">Bound canonical</th>
                            <th className="text-right px-2 py-1 font-semibold text-grey-700">Fails / Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.bucket_a_misbinding_examples.map((c) => (
                            <tr key={c.announcement_id} className="border-b border-grey-100">
                              <td className="px-2 py-1 font-mono text-grey-700 truncate max-w-[120px]">{c.announcement_id.slice(0, 8)}…</td>
                              <td className="px-2 py-1 text-grey-600">{c.announcement_date || '—'}</td>
                              <td className="px-2 py-1 text-amber-700 font-medium">{c.announcement_company_name}</td>
                              <td className="px-2 py-1 font-mono text-grey-700">{c.bound_ticker || '—'}</td>
                              <td className="px-2 py-1 text-grey-700">{c.bound_canonical_name}</td>
                              <td className="px-2 py-1 text-right font-mono text-grey-500">{c.entity_fail_guard_count} / {c.entity_total_announcements}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          {runState === 'error' && (
            <div className="flex items-center gap-2 text-sm text-red-600">
              <XCircle className="h-4 w-4" />
              <span>{error || 'Failed'}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function EntityMismatchResolve() {
  const [runState, setRunState] = useState<'idle' | 'submitting' | 'running' | 'done' | 'error'>('idle');
  const [runId, setRunId] = useState<string | null>(null);
  const [mode, setMode] = useState<'dry' | 'execute'>('dry');
  const [result, setResult] = useState<EntityMismatchResolveResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'b' | 'a'>('b');
  const [useExternal, setUseExternal] = useState(true);
  const [applyMedium, setApplyMedium] = useState(false);
  const runIdRef = useRef<string | null>(null);
  useEffect(() => { runIdRef.current = runId; }, [runId]);

  // Subscribed through `onEvent` — useWebSocket returns `{ connected }` and
  // nothing else. This card previously destructured a `lastMessage` that has
  // never existed, so the whole handler was dead and the poll fallback below
  // was doing all the work. Registered once, so it reads live values through
  // refs only.
  //
  // `results` is OPTIONAL on the emitted payload (_emit_tools_status only
  // includes it when the caller passes one), so completion falls back to the
  // run record rather than blanking a real result.
  const handleToolStatus = useCallback((payload: any) => {
    if (!payload || payload.tool_name !== 'mismatch_resolve') return;
    if (!runIdRef.current || payload.run_id !== runIdRef.current) return;
    if (payload.status === 'completed') {
      if (payload.results) {
        setResult(payload.results);
        setRunState('done');
      } else {
        getToolRunStatus(payload.run_id)
          .then((s) => { setResult((s.result as EntityMismatchResolveResult) ?? null); setRunState('done'); })
          .catch(() => setRunState('done'));
      }
    } else if (payload.status === 'failed') {
      setError(payload.error || 'Job failed');
      setRunState('error');
    } else if (payload.status === 'running') {
      setRunState('running');
    }
  }, []);

  useWebSocket({
    channels: ['assayerai:tools'],
    autoInvalidate: false,
    onEvent: { tool_status: handleToolStatus },
  });

  useEffect(() => {
    if (runState !== 'submitting' && runState !== 'running') return;
    if (!runId) return;
    let cancelled = false;
    (async () => {
      for (let i = 0; i < 60; i++) {
        await new Promise((r) => setTimeout(r, 5000));
        if (cancelled) return;
        try {
          const s: any = await getToolRunStatus(runId);
          if (s.status === 'completed') {
            setResult(s.result || null);
            setRunState('done');
            return;
          }
          if (s.status === 'failed') {
            setError(s.error || 'Job failed');
            setRunState('error');
            return;
          }
        } catch { /* keep polling */ }
      }
    })();
    return () => { cancelled = true; };
  }, [runState, runId]);

  const run = async (m: 'dry' | 'execute') => {
    if (m === 'execute') {
      const stats = result?.stats;
      const autoCount = stats
        ? (stats.bucket_a.high + stats.bucket_a.promoted +
           stats.bucket_b.high + stats.bucket_b.promoted +
           (applyMedium ? stats.bucket_a.medium + stats.bucket_b.medium : 0))
        : '(unknown — re-run Dry first)';
      if (!window.confirm(
        `Execute auto-resolution?\n\n` +
        `Will apply ${autoCount} fix plans (High + Promoted${applyMedium ? ' + Medium' : ''}).\n` +
        `Bucket A: re-binds announcements to the EEC-correct entity.\n` +
        `Bucket B: renames Assayer entity_local_state.display_name AND posts a ` +
        `direct-rename to Cynapse (UPDATE canonical_name + NULL embedding + ` +
        `DELETE contamination legal_name alias). No rebrand alias is written.\n\n` +
        `Cross-DB, no global transaction. If a Cynapse POST fails mid-plan, ` +
        `the plan records the half-state and the next run will re-detect & retry.`
      )) return;
    }
    setRunState('submitting');
    setMode(m);
    setError(null);
    setResult(null);
    setRunId(null);
    try {
      const sub = await submitEntityMismatchResolve({
        dry_run: m === 'dry',
        apply_medium: applyMedium,
        use_external_lookup: useExternal,
        limit: 200,
      });
      setRunId(sub.run_id);
      setRunState('running');
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Request failed');
      setRunState('error');
    }
  };

  const tierColour = (tier: string) => {
    if (tier === 'high') return 'bg-success-50 text-success-700';
    if (tier === 'promoted') return 'bg-blue-50 text-blue-700';
    if (tier === 'medium') return 'bg-amber-50 text-amber-700';
    return 'bg-grey-50 text-grey-700';
  };

  return (
    <div className="bg-surface border border-grey-200 rounded-lg p-6 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3 flex-1">
          <div className="mt-0.5 text-grey-500"><Sparkles className="h-5 w-5" /></div>
          <div>
            <h3 className="font-semibold text-grey-900">Auto-Resolve Mismatch Candidates (Phase 4)</h3>
            <p className="text-sm text-grey-600 mt-1">
              Two-pass auto-resolver. <strong>4a</strong> uses Phase 2 EEC ground-truth + Phase 1 name guard
              to re-bind misbound announcements. <strong>4b</strong> uses majority-vote ≥80% + ≥5 anns to rename
              contaminated canonicals via direct SQL (NO rebrand alias per
              <code className="text-xs bg-grey-100 px-1 rounded">feedback_contamination_vs_rebrand_fork</code>).
              Borderline candidates promoted by external lookup (marketindex via Firecrawl).
              <br/>
              <strong>Dry Run</strong> previews tier + proposed SQL per candidate.
              <strong> Execute</strong> applies HIGH + PROMOTED by default; opt-in to MEDIUM. Cross-DB, idempotent on retry.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap sm:ml-4 flex-shrink-0">
          <button
            onClick={() => run('dry')}
            disabled={runState === 'submitting' || runState === 'running'}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-grey-300 text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Eye className="h-4 w-4" /> Dry Run
          </button>
          <button
            onClick={() => run('execute')}
            disabled={runState === 'submitting' || runState === 'running'}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Play className="h-4 w-4" /> Execute
          </button>
        </div>
      </div>

      <div className="mt-3 flex items-center gap-4 text-xs">
        <label className="flex items-center gap-1.5 text-grey-700 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={useExternal}
            onChange={(e) => setUseExternal(e.target.checked)}
            disabled={runState === 'submitting' || runState === 'running'}
            className="rounded border-grey-300"
          />
          External lookup (promote borderline via marketindex)
        </label>
        <label className="flex items-center gap-1.5 text-grey-700 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={applyMedium}
            onChange={(e) => setApplyMedium(e.target.checked)}
            disabled={runState === 'submitting' || runState === 'running'}
            className="rounded border-grey-300"
          />
          Apply MEDIUM tier too (operator opt-in)
        </label>
      </div>

      {runState !== 'idle' && (
        <div className="mt-4 pt-4 border-t border-grey-100">
          {(runState === 'submitting' || runState === 'running') && (
            <div className="flex items-center gap-2 text-sm text-primary-600">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>
                {runState === 'submitting'
                  ? 'Submitting…'
                  : mode === 'dry'
                    ? 'Building plans server-side — safe to navigate away'
                    : 'Applying plans server-side — safe to navigate away'}
              </span>
              {runId && <span className="text-xs text-grey-400 font-mono ml-2">{runId.slice(0, 8)}…</span>}
            </div>
          )}
          {runState === 'done' && result && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-success-700">
                <CheckCircle className="h-4 w-4" />
                <span>{mode === 'dry' ? 'Dry run complete' : 'Execution complete'}</span>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {(['bucket_a', 'bucket_b'] as const).map((bk) => {
                  const s = result.stats[bk];
                  return (
                    <div key={bk} className="bg-grey-50 rounded p-3">
                      <div className="text-xs uppercase tracking-wide text-grey-500 font-semibold mb-2">
                        {bk === 'bucket_a' ? 'Bucket A — wrong binding' : 'Bucket B — wrong canonical'}
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-1 text-xs">
                        <div><div className="text-grey-500">Total</div><div className="font-mono font-semibold">{s.total}</div></div>
                        <div><div className="text-grey-500">High</div><div className="font-mono font-semibold text-success-700">{s.high}</div></div>
                        <div><div className="text-grey-500">Promo</div><div className="font-mono font-semibold text-blue-700">{s.promoted}</div></div>
                        <div><div className="text-grey-500">Med</div><div className="font-mono font-semibold text-amber-700">{s.medium}</div></div>
                        <div><div className="text-grey-500">Low</div><div className="font-mono font-semibold text-grey-700">{s.low}</div></div>
                        <div><div className="text-grey-500">Applied</div><div className="font-mono font-semibold text-success-700">{s.applied}</div></div>
                        <div><div className="text-grey-500">Errors</div><div className="font-mono font-semibold text-red-700">{s.errors}</div></div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex items-center gap-2 border-b border-grey-200">
                <button
                  onClick={() => setActiveTab('b')}
                  className={`text-xs px-3 py-2 font-medium border-b-2 transition-colors ${
                    activeTab === 'b'
                      ? 'border-red-600 text-red-700'
                      : 'border-transparent text-grey-600 hover:text-grey-900'
                  }`}
                >
                  Bucket B plans ({result.bucket_b_plans.length})
                </button>
                <button
                  onClick={() => setActiveTab('a')}
                  className={`text-xs px-3 py-2 font-medium border-b-2 transition-colors ${
                    activeTab === 'a'
                      ? 'border-amber-600 text-amber-700'
                      : 'border-transparent text-grey-600 hover:text-grey-900'
                  }`}
                >
                  Bucket A plans ({result.bucket_a_plans.length})
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-grey-50">
                    <tr>
                      <th className="text-left px-2 py-1 font-semibold text-grey-700">Tier</th>
                      <th className="text-left px-2 py-1 font-semibold text-grey-700">Candidate</th>
                      <th className="text-left px-2 py-1 font-semibold text-grey-700">Ticker</th>
                      <th className="text-left px-2 py-1 font-semibold text-grey-700">Proposed action</th>
                      <th className="text-left px-2 py-1 font-semibold text-grey-700">Reason</th>
                      <th className="text-left px-2 py-1 font-semibold text-grey-700">External evidence</th>
                      <th className="text-left px-2 py-1 font-semibold text-grey-700">Applied</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(activeTab === 'b' ? result.bucket_b_plans : result.bucket_a_plans).map((p) => {
                      const evidenceText = p.external_evidence
                        ? (p.external_evidence.canonical_name
                           ? `${p.external_evidence.canonical_name}${p.external_evidence.status && p.external_evidence.status !== 'active' ? ` (${p.external_evidence.status})` : ''}`
                           : `fetch:${p.external_evidence.fetch_status}`)
                        : '—';
                      // Only render a "proposed action" string when the plan
                      // is actionable (high / promoted / medium). For LOW-tier
                      // plans, the rename / re-bind target is either absent
                      // (e.g. too_few_announcements skips the majority
                      // computation entirely) or unreliable (no clear majority,
                      // ticker-shaped, etc). Show the reason in this column
                      // instead so operators don't read a misleading proposal.
                      let action: string;
                      if (p.tier === 'low') {
                        action = '—';
                      } else if (p.bucket === 'B') {
                        const proposed = p.internal_evidence.best_cased_majority
                          || p.internal_evidence.majority_name
                          || '?';
                        action = `rename "${p.internal_evidence.stored_canonical_name}" → "${proposed}"`;
                      } else {
                        const target = p.internal_evidence.correct_display_name
                          || p.internal_evidence.alternative_display_name
                          || p.internal_evidence.eec_resolved_assayer_entity
                          || 'unknown';
                        action = `re-bind to ${target}`;
                      }
                      return (
                        <tr key={`${p.bucket}-${p.candidate_id}`} className="border-b border-grey-100 align-top">
                          <td className="px-2 py-1">
                            <span className={`inline-block px-1.5 py-0.5 rounded font-semibold ${tierColour(p.tier)}`}>
                              {p.tier.toUpperCase()}
                            </span>
                          </td>
                          <td className="px-2 py-1 font-mono text-grey-700 truncate max-w-[110px]">{p.candidate_id.slice(0, 8)}…</td>
                          <td className="px-2 py-1 font-mono text-grey-700">{p.ticker || '—'}</td>
                          <td className="px-2 py-1 text-grey-800">{action}</td>
                          <td className="px-2 py-1 text-grey-600">{p.reason}</td>
                          <td className="px-2 py-1 text-grey-700">
                            {evidenceText}
                            {p.external_evidence?.source_url && (
                              <a href={p.external_evidence.source_url} target="_blank" rel="noreferrer" className="ml-1 text-blue-600 hover:underline">↗</a>
                            )}
                          </td>
                          <td className="px-2 py-1">
                            {p.applied ? (
                              <span className="text-success-700">✓</span>
                            ) : p.apply_error ? (
                              <span className="text-red-700" title={p.apply_error}>✗</span>
                            ) : (
                              <span className="text-grey-400">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {runState === 'error' && (
            <div className="flex items-center gap-2 text-sm text-red-600">
              <XCircle className="h-4 w-4" />
              <span>{error || 'Failed'}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function TextImageHeroRemediation() {
  const [runState, setRunState] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const [dryRun, setDryRun] = useState(true);
  const [result, setResult] = useState<RemediateTextImageHeroesResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async (dry: boolean) => {
    setRunState('running');
    setDryRun(dry);
    setError(null);
    setResult(null);
    try {
      const data = await remediateTextImageHeroes(dry, 200);
      setResult(data);
      setRunState('done');
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Request failed');
      setRunState('error');
    }
  };

  return (
    <div className="bg-surface border border-grey-200 rounded-lg p-6 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3 flex-1">
          <div className="mt-0.5 text-grey-500"><ImageIcon className="h-5 w-5" /></div>
          <div>
            <h3 className="font-semibold text-grey-900">Remediate Text-Image Heroes</h3>
            <p className="text-sm text-grey-600 mt-1">
              Finds articles whose hero image is a PDF page-render and re-runs
              image preparation: a screenshot of body text is dropped and
              replaced with a real photo/figure or an entity-pool fallback (a
              genuine chart page-render is left as the hero). Article prose is
              untouched. Processes every page-render hero in one run — Dry Run
              first to see the count. Idempotent; safe to re-run.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap sm:ml-4 flex-shrink-0">
          <button
            onClick={() => run(true)}
            disabled={runState === 'running'}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-grey-300 text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Eye className="h-4 w-4" />
            Dry Run
          </button>
          <button
            onClick={() => run(false)}
            disabled={runState === 'running'}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Play className="h-4 w-4" />
            Execute
          </button>
        </div>
      </div>
      {runState !== 'idle' && (
        <div className="mt-4 pt-4 border-t border-grey-100">
          {runState === 'running' && (
            <div className="flex items-center gap-2 text-sm text-primary-600">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Running{dryRun ? ' (dry run)' : ''}…</span>
            </div>
          )}
          {runState === 'done' && result && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-success-700">
                <CheckCircle className="h-4 w-4" />
                <span>{dryRun ? 'Dry run complete' : 'Completed'}</span>
              </div>
              <pre className="text-xs bg-grey-50 rounded-md p-3 overflow-x-auto text-grey-800 font-mono">
                {JSON.stringify(result, null, 2)}
              </pre>
            </div>
          )}
          {runState === 'error' && (
            <div className="flex items-center gap-2 text-sm text-red-600">
              <XCircle className="h-4 w-4" />
              <span>{error || 'Failed'}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


/**
 * CT-3 tranche 3a — per-entity coverage consolidation.
 *
 * Ownership is decided per announcement, so a company whose filings scatter
 * across commodity buckets gets a different indexed owner per filing and
 * neither masthead accumulates a coherent body of coverage. This brings one
 * entity's already-published coverage under the masthead its ENTITY sector
 * says owns it.
 *
 * Two rails, deliberately asymmetric: demotions apply freely (the page stays
 * live and hub-linked, it just carries noindex), promotions never do — a
 * pre-Track-I copy has no verdict at all, so it runs the quality gate first
 * and earns the index tier or keeps noindex on the answer.
 *
 * The run is a BackgroundTask on the backend, so the card follows the
 * house async pattern: submit → remember the run_id → WebSocket for the
 * status transition, polling for progress, and mount-time hydration so
 * navigating away mid-run doesn't lose it.
 */

// Own key in the shared run registry. Deliberately NOT in TOOLS: this card
// owns its lifecycle (it needs a selection before it can run), and
// ToolsPage's generic hydration skips ids it doesn't recognise.
const CONSOLIDATION_TOOL_ID = 'consolidate_entity_coverage';

function phaseLabel(p: ConsolidationProgress | null, dryRun: boolean): string {
  if (!p) return 'Starting…';
  const entity = p.entities_total && p.entities_total > 1
    ? ` (company ${Math.min((p.entities_processed ?? 0) + 1, p.entities_total)} of ${p.entities_total})`
    : '';
  switch (p.phase) {
    case 'queued':
      return 'Queued…';
    case 'starting':
      return `Loading coverage${entity}…`;
    case 'gating':
      // The slow one, and the only phase that costs money — say so.
      return `Running the quality gate on article ${(p.articles_processed ?? 0) + 1}`
        + ` of ${p.articles_total ?? '?'}${entity}…`;
    case 'planning':
      return `Assessing article ${(p.articles_processed ?? 0) + 1}`
        + ` of ${p.articles_total ?? '?'}${entity}…`;
    case 'entity_complete':
      return `${p.entities_processed ?? 0} of ${p.entities_total ?? 0} companies done…`;
    case 'complete':
      return dryRun ? 'Dry run complete' : 'Complete';
    default:
      return 'Working…';
  }
}

function CoverageConsolidation() {
  const [set, setSet] = useState<ConsolidationSet | null>(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [runState, setRunState] = useState<'idle' | 'submitting' | 'running' | 'done' | 'error'>('idle');
  const [runId, setRunId] = useState<string | null>(null);
  const [dryRun, setDryRun] = useState(true);
  const [progress, setProgress] = useState<ConsolidationProgress | null>(null);
  const [result, setResult] = useState<Record<string, any> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const runIdRef = useRef<string | null>(null);

  useEffect(() => { runIdRef.current = runId; }, [runId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setSet(await getConsolidationCandidates());
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Request failed');
    } finally {
      setLoading(false);
    }
  }, []);

  const finishFromStatus = useCallback((s: ToolRunStatus) => {
    setResult(s.result);
    setProgress((s.progress as ConsolidationProgress) ?? null);
    setRunState('done');
    forgetRun(CONSOLIDATION_TOOL_ID);
  }, []);

  // Mount-time hydration: a run persisted by an earlier visit is still
  // going (or finished while we were away). 404 means the backend
  // restarted — _active_tools is in-memory — so drop the stale entry.
  useEffect(() => {
    const stored = loadStoredRuns()[CONSOLIDATION_TOOL_ID];
    if (!stored) return;
    let cancelled = false;
    (async () => {
      try {
        const s = await getToolRunStatus(stored.runId);
        if (cancelled) return;
        setRunId(stored.runId);
        setDryRun(stored.dryRun);
        setProgress((s.progress as ConsolidationProgress) ?? null);
        if (s.status === 'completed') finishFromStatus(s);
        else if (s.status === 'failed') {
          setError(s.error || 'Run failed');
          setRunState('error');
          forgetRun(CONSOLIDATION_TOOL_ID);
        } else setRunState('running');
      } catch {
        if (!cancelled) forgetRun(CONSOLIDATION_TOOL_ID);
      }
    })();
    return () => { cancelled = true; };
  }, [finishFromStatus]);

  // The shared tools channel carries the status TRANSITION; progress comes
  // from polling below.
  //
  // ⚠ Subscribed via `onEvent`, NOT via a `lastMessage` return value —
  // useWebSocket returns `{ connected }` only. The four older cards on this
  // page destructure `lastMessage`, get `undefined`, and their WebSocket
  // branches never execute (they run entirely on their poll fallbacks). Do
  // not copy that shape here.
  //
  // The handler is registered once, so it must read live values through refs
  // and stable callbacks — never through captured state. The event payload
  // carries no result for this tool, so completion re-reads the run record
  // rather than trusting the event to carry one.
  const handleToolStatus = useCallback((payload: any) => {
    if (!payload || payload.tool_name !== CONSOLIDATION_TOOL_ID) return;
    if (!runIdRef.current || payload.run_id !== runIdRef.current) return;
    if (payload.status === 'completed') {
      getToolRunStatus(payload.run_id)
        .then(finishFromStatus)
        .catch(() => {
          setRunState('done');
          forgetRun(CONSOLIDATION_TOOL_ID);
        });
    } else if (payload.status === 'failed') {
      setError(payload.error || 'Run failed');
      setRunState('error');
      forgetRun(CONSOLIDATION_TOOL_ID);
    } else if (payload.status === 'running') {
      setRunState('running');
    }
  }, [finishFromStatus]);

  useWebSocket({
    channels: ['assayerai:tools'],
    autoInvalidate: false,
    onEvent: { tool_status: handleToolStatus },
  });

  // Poll while mounted and running. This is not only a WebSocket fallback:
  // progress lives in the run record, so polling is what makes the bar
  // move. The cap is generous on purpose — an execute may run the quality
  // gate up to max_gate_runs times, and a 5-minute cap would strand the UI
  // at "running" while the backend was still working.
  useEffect(() => {
    if (runState !== 'submitting' && runState !== 'running') return;
    if (!runId) return;
    let cancelled = false;
    (async () => {
      for (let i = 0; i < 900; i++) {
        await new Promise((r) => setTimeout(r, 4000));
        if (cancelled) return;
        try {
          const s = await getToolRunStatus(runId);
          if (cancelled) return;
          if (s.progress) setProgress(s.progress as ConsolidationProgress);
          if (s.status === 'completed') { finishFromStatus(s); return; }
          if (s.status === 'failed') {
            setError(s.error || 'Run failed');
            setRunState('error');
            forgetRun(CONSOLIDATION_TOOL_ID);
            return;
          }
        } catch { /* transient — keep polling */ }
      }
    })();
    return () => { cancelled = true; };
  }, [runState, runId, finishFromStatus]);

  // Refresh the set once an EXECUTE lands: the counts it shows are exactly
  // what the run just changed.
  useEffect(() => {
    if (runState === 'done' && !dryRun) void load();
  }, [runState, dryRun, load]);

  const toggle = (entityId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(entityId)) next.delete(entityId);
      else next.add(entityId);
      return next;
    });
  };

  const run = async (dry: boolean) => {
    if (selected.size === 0) return;
    // Re-triggering forgets any prior persisted run so the next visit
    // doesn't hydrate a stale one.
    forgetRun(CONSOLIDATION_TOOL_ID);
    setRunState('submitting');
    setDryRun(dry);
    setError(null);
    setResult(null);
    setProgress(null);
    setRunId(null);
    try {
      const submission = await submitConsolidateEntityCoverage(
        Array.from(selected), dry,
      );
      setRunId(submission.run_id);
      setRunState('running');
      rememberRun(CONSOLIDATION_TOOL_ID, submission.run_id, dry);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Request failed');
      setRunState('error');
    }
  };

  const running = runState === 'submitting' || runState === 'running';
  const entitiesTotal = progress?.entities_total ?? 0;
  const entitiesDone = progress?.entities_processed ?? 0;
  const pct = entitiesTotal > 0
    ? Math.min(100, Math.round((entitiesDone / entitiesTotal) * 100))
    : 0;
  // The runner only knows ids mid-run; the names live in the loaded set.
  const currentName = progress?.current_entity_id
    ? (set?.eligible.concat(set?.held_for_triage || [])
        .find((c) => c.entity_id === progress.current_entity_id)?.display_name
        || null)
    : null;
  const liveTotals = progress?.totals || null;

  const row = (c: ConsolidationCandidate, selectable: boolean) => (
    <tr key={c.entity_id} className="border-t border-grey-100">
      <td className="py-1.5 pr-3">
        {selectable ? (
          <input
            type="checkbox"
            checked={selected.has(c.entity_id)}
            onChange={() => toggle(c.entity_id)}
            disabled={running}
            className="rounded border-grey-300"
          />
        ) : null}
      </td>
      <td className="py-1.5 pr-3 text-grey-900">{c.display_name || c.entity_id}</td>
      <td className="py-1.5 pr-3 text-grey-700">{c.entity_sector || '—'}</td>
      <td className="py-1.5 pr-3 text-grey-500 tabular-nums">
        {c.sector_confidence === null ? '—' : c.sector_confidence.toFixed(2)}
        <span className="text-grey-400"> · {c.sector_source || 'none'}</span>
      </td>
      <td className="py-1.5 pr-3 tabular-nums text-grey-700">{c.published_articles}</td>
      <td className="py-1.5 pr-3 tabular-nums text-grey-700">{c.would_demote}</td>
      <td className="py-1.5 pr-3 tabular-nums text-grey-700">{c.promote_candidates}</td>
      <td className="py-1.5 text-grey-600">{c.owners_now.join(', ')}</td>
    </tr>
  );

  const header = (
    <thead>
      <tr className="text-left text-grey-500">
        <th className="pb-1.5 pr-3 w-6"></th>
        <th className="pb-1.5 pr-3 font-medium">Company</th>
        <th className="pb-1.5 pr-3 font-medium">Sector</th>
        <th className="pb-1.5 pr-3 font-medium">Confidence</th>
        <th className="pb-1.5 pr-3 font-medium">Articles</th>
        <th className="pb-1.5 pr-3 font-medium">Demote</th>
        <th className="pb-1.5 pr-3 font-medium">Promote</th>
        <th className="pb-1.5 font-medium">Owner</th>
      </tr>
    </thead>
  );

  return (
    <div className="bg-surface border border-grey-200 rounded-lg p-6 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="mt-0.5 text-grey-500"><Layers className="h-5 w-5" /></div>
          <div className="min-w-0">
            <h3 className="font-semibold text-grey-900">Consolidate Entity Coverage (CT-3)</h3>
            <p className="text-sm text-grey-600 mt-1">
              Brings a company's published coverage under the ONE masthead its
              entity sector says owns it, so its hub accumulates instead of
              splitting. Non-owner copies stay live and hub-linked, carrying
              noindex — no URL moves, no redirects. An owner copy that has
              never been quality-gated runs the gate here and earns the index
              tier or keeps noindex on the verdict. Dry Run costs nothing: it
              writes nothing and runs no gate. Executing republishes to the
              live site.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap sm:ml-4 flex-shrink-0">
          <button
            onClick={load}
            disabled={loading || running}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-grey-300 text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Load set
          </button>
          <button
            onClick={() => run(true)}
            disabled={selected.size === 0 || running}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-grey-300 text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Eye className="h-4 w-4" />
            Dry Run ({selected.size})
          </button>
          <button
            onClick={() => run(false)}
            disabled={selected.size === 0 || running}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Play className="h-4 w-4" />
            Consolidate ({selected.size})
          </button>
        </div>
      </div>

      {set && (
        <div className="mt-4 pt-4 border-t border-grey-100 space-y-4">
          <p className="text-sm text-grey-600">
            <span className="font-medium text-grey-900">{set.entities_in_set}</span> companies
            change owner under the entity axis, covering{' '}
            <span className="font-medium text-grey-900">{set.articles_in_set}</span> published
            articles. {set.held_for_triage_count} are held below the{' '}
            {set.confidence_floor.toFixed(2)} sector-confidence floor.
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              {header}
              <tbody>{set.eligible.map((c) => row(c, true))}</tbody>
            </table>
          </div>
          {set.held_for_triage.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-grey-900 mt-4">
                Held for operator triage — sector not trusted enough to move coverage
              </h4>
              <p className="text-xs text-grey-500 mt-0.5 mb-2">
                Set the sector by hand on the entity (an operator override is
                never overwritten by the resolver), then load the set again.
              </p>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  {header}
                  <tbody>{set.held_for_triage.map((c) => row(c, false))}</tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {runState !== 'idle' && (
        <div className="mt-4 pt-4 border-t border-grey-100">
          {running && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-primary-600">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>
                  {phaseLabel(progress, dryRun)}
                  {dryRun ? ' (dry run)' : ''}
                </span>
              </div>
              {currentName && (
                <div className="text-xs text-grey-500">{currentName}</div>
              )}
              {entitiesTotal > 0 && (
                <div className="h-1.5 w-full rounded-full bg-grey-100 overflow-hidden">
                  <div
                    className="h-full bg-primary-600 transition-all duration-500"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              )}
              {liveTotals && (
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-grey-600 tabular-nums">
                  <span>demoted {liveTotals.demoted ?? 0}</span>
                  <span>promoted {liveTotals.promoted ?? 0}</span>
                  <span>stamped {liveTotals.stamped_provenance ?? 0}</span>
                  <span>gated {liveTotals.gated ?? 0}</span>
                  <span>republished {liveTotals.republished ?? 0}</span>
                  {(liveTotals.errors ?? 0) > 0 && (
                    <span className="text-red-600">errors {liveTotals.errors}</span>
                  )}
                </div>
              )}
              <p className="text-xs text-grey-400">
                Runs on the backend — you can navigate away and come back.
              </p>
            </div>
          )}
          {runState === 'done' && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-success-700">
                <CheckCircle className="h-4 w-4" />
                <span>{dryRun ? 'Dry run complete' : 'Completed'}</span>
              </div>
              <pre className="text-xs bg-grey-50 rounded-md p-3 overflow-x-auto text-grey-800 font-mono max-h-96">
                {JSON.stringify(result, null, 2)}
              </pre>
            </div>
          )}
          {runState === 'error' && (
            <div className="flex items-center gap-2 text-sm text-red-600">
              <XCircle className="h-4 w-4" />
              <span>{error || 'Failed'}</span>
            </div>
          )}
        </div>
      )}
      {error && runState !== 'error' && (
        <div className="mt-4 flex items-center gap-2 text-sm text-red-600">
          <XCircle className="h-4 w-4" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}


export function ToolsPage() {
  const [toolStates, setToolStates] = useState<Record<string, ToolState>>({});
  const [activeTab, setActiveTab] = useState<'active' | 'archive'>('active');

  // Mount-time hydration — for every persisted run_id, ask the backend
  // for its current status and seed toolStates accordingly. Runs once
  // per mount; doesn't fight the WebSocket subscription (the subscription
  // only delivers future events).
  useEffect(() => {
    let cancelled = false;
    const stored = loadStoredRuns();
    if (Object.keys(stored).length === 0) return;
    (async () => {
      for (const [toolId, info] of Object.entries(stored)) {
        // Cards that own their own lifecycle (CoverageConsolidation) share
        // this registry but not this machinery — hydrating them here would
        // seed a toolState nothing renders and start a second poll loop
        // against the same run.
        if (!TOOLS.some((t) => t.id === toolId)) continue;
        try {
          const status: ToolRunStatus = await getToolRunStatus(info.runId);
          if (cancelled) return;
          let runState: RunState;
          if (status.status === 'completed') runState = 'completed';
          else if (status.status === 'failed') runState = 'failed';
          else runState = 'running';
          setToolStates((prev) => ({
            ...prev,
            [toolId]: {
              runState,
              runId: info.runId,
              dryRun: info.dryRun,
              result: status.result,
              error: status.error,
            },
          }));
          // Resume polling if still running so we catch completion
          // even if the WebSocket event fired while we were away.
          if (runState === 'running') {
            pollJobRef.current?.(toolId, info.runId);
          }
        } catch {
          // 404 means the backend has restarted (in-memory store
          // cleared) or the run is older than retention. Drop the
          // stored entry so we don't keep retrying.
          if (!cancelled) forgetRun(toolId);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hold a ref to pollJob so the hydration effect above can call it
  // without depending on `toolStates` (which would re-trigger on every
  // state change).
  const pollJobRef = useRef<((toolId: string, runId: string) => Promise<void>) | null>(null);

  // Tool status over the shared WS channel.
  //
  // ⚠ Subscribed through `onEvent`. This handler used to hang off a
  // `lastMessage` value that useWebSocket has never returned (it returns
  // `{ connected }`), so it was dead code and every tile has been running on
  // the 5-second poll fallback below. The handler is registered ONCE, so it
  // may only read live values through the functional setState form — never
  // through captured state.
  //
  // Tiles are keyed by `tool.id` and events by `tool_name`; verified equal
  // for all 18 tiles, so the lookup lands. A tile whose run_id doesn't match
  // is left alone, which is what keeps two tabs from fighting.
  //
  // `results` is OPTIONAL on the payload — `_emit_tools_status` only includes
  // it when the caller passes one, and several tools don't. Trusting it
  // blindly would blank a real result the poll had already fetched, so a
  // completion without results re-reads the run record instead.
  const handleToolStatus = useCallback((payload: any) => {
    const toolName = payload?.tool_name;
    if (!toolName) return;

    // Whichever path resolves the tile first wins. The poll already exits
    // early when the WS beat it; this is the same guard from the other side,
    // so a late event cannot re-write a settled tile with a differently
    // shaped result (the emitted `results` block and the stored `result` are
    // not always the same object).
    const isTerminal = (st?: ToolState) =>
      st?.runState === 'completed' || st?.runState === 'failed';

    if (payload.status === 'completed' && !payload.results) {
      getToolRunStatus(payload.run_id)
        .then((s) => {
          setToolStates((prev) => {
            const current = prev[toolName];
            if (!current || current.runId !== payload.run_id) return prev;
            if (isTerminal(current)) return prev;
            return {
              ...prev,
              [toolName]: { ...current, runState: 'completed', result: s.result },
            };
          });
        })
        .catch(() => { /* the poll fallback still has it */ });
      return;
    }

    setToolStates((prev) => {
      const current = prev[toolName];
      if (!current || current.runId !== payload.run_id) return prev;
      if (isTerminal(current)) return prev;

      if (payload.status === 'completed') {
        return {
          ...prev,
          [toolName]: {
            ...current,
            runState: 'completed',
            result: payload.results,
          },
        };
      }
      if (payload.status === 'failed') {
        return {
          ...prev,
          [toolName]: {
            ...current,
            runState: 'failed',
            error: payload.error || 'Job failed',
          },
        };
      }
      return prev;
    });
  }, []);

  // Progress events (emitted by long tools every few rows). Only ever
  // updates a tile that is currently running THIS run_id.
  const handleToolProgress = useCallback((payload: any) => {
    const toolName = payload?.tool_name;
    if (!toolName) return;
    setToolStates((prev) => {
      const current = prev[toolName];
      if (!current || current.runId !== payload.run_id) return prev;
      if (current.runState !== 'running') return prev;
      return { ...prev, [toolName]: { ...current, progress: payload } };
    });
  }, []);

  useWebSocket({
    channels: ['assayerai:tools'],
    autoInvalidate: false,
    onEvent: { tool_status: handleToolStatus, tool_progress: handleToolProgress },
  });

  // Poll for job status as fallback (WebSocket may not always deliver)
  const pollJob = useCallback(async (toolId: string, runId: string) => {
    const maxAttempts = 60; // 5 minutes at 5s intervals
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise((r) => setTimeout(r, 5000));

      // Check if already completed via WebSocket
      const current = toolStates[toolId];
      if (current?.runState === 'completed' || current?.runState === 'failed') return;

      try {
        const status: ToolRunStatus = await getToolRunStatus(runId);
        if (status.status === 'completed') {
          setToolStates((prev) => ({
            ...prev,
            [toolId]: {
              ...prev[toolId],
              runState: 'completed',
              result: status.result,
            },
          }));
          return;
        }
        if (status.status === 'failed') {
          setToolStates((prev) => ({
            ...prev,
            [toolId]: {
              ...prev[toolId],
              runState: 'failed',
              error: 'Job failed — check logs',
            },
          }));
          return;
        }
      } catch {
        // Ignore polling errors, keep trying
      }
    }
  }, [toolStates]);

  // Expose pollJob to the mount-time hydration effect via ref.
  useEffect(() => {
    pollJobRef.current = pollJob;
  }, [pollJob]);

  const handleRun = async (tool: ToolDefinition, dryRun: boolean) => {
    // Re-triggering forgets any prior persisted run for this tool so
    // hydration on the next visit doesn't surface the stale entry.
    forgetRun(tool.id);
    setToolStates((prev) => ({
      ...prev,
      [tool.id]: { runState: 'submitting', runId: null, dryRun, result: null, error: null },
    }));

    try {
      const submission = await tool.run(dryRun);
      setToolStates((prev) => ({
        ...prev,
        [tool.id]: { runState: 'running', runId: submission.run_id, dryRun, result: null, error: null },
      }));
      // Persist for cross-navigation resume. The hydration effect on
      // ToolsPage mount will fetch this run's current status via
      // GET /tools/runs/{run_id} and restore the tile.
      rememberRun(tool.id, submission.run_id, dryRun);
      // Start polling as fallback
      pollJob(tool.id, submission.run_id);
    } catch (err: any) {
      setToolStates((prev) => ({
        ...prev,
        [tool.id]: {
          runState: 'failed',
          runId: null,
          dryRun,
          result: null,
          error: err?.response?.data?.detail || err.message || 'Submission failed',
        },
      }));
    }
  };

  const visibleTools = TOOLS.filter((t) =>
    activeTab === 'archive' ? !!t.archive : !t.archive,
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Wrench className="h-7 w-7 text-grey-700" />
        <h1 className="text-2xl font-bold text-grey-900">Tools</h1>
      </div>
      <p className="text-grey-600">
        Maintenance and repair tools. Use Dry Run to preview changes before applying.
      </p>

      {/* Tab switcher — Active vs Archive */}
      <div className="border-b border-grey-200">
        <div className="flex items-center gap-1">
          <button
            onClick={() => setActiveTab('active')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'active'
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-grey-600 hover:text-grey-900'
            }`}
          >
            Active
          </button>
          <button
            onClick={() => setActiveTab('archive')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'archive'
                ? 'border-grey-600 text-grey-800'
                : 'border-transparent text-grey-500 hover:text-grey-800'
            }`}
          >
            Archive
          </button>
        </div>
      </div>

      {activeTab === 'archive' && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900">
          <strong>Archive tab.</strong> Tools below were built for specific incidents
          or migrations that have completed their primary purpose. They are kept here
          for re-runnability + reference. Active operational tools live in the{' '}
          <button
            onClick={() => setActiveTab('active')}
            className="underline text-amber-900 hover:text-amber-700"
          >
            Active tab
          </button>.
        </div>
      )}

      {activeTab === 'active' && (
        <>
          <section className="bg-surface border border-grey-200 rounded-lg p-4 shadow-sm">
            <Link
              to="/triage"
              className="flex items-center justify-between gap-3 group"
            >
              <div>
                <h2 className="text-lg font-semibold text-grey-900 group-hover:text-primary-700">
                  Discovery Triage →
                </h2>
                <p className="text-sm text-grey-600 mt-1">
                  Promote skipped scans, demote queued ones, with bulk operations
                  across the last two trading days. Moved to its own page so the
                  operator workflow is one click away.
                </p>
              </div>
              <ArrowRight className="h-5 w-5 text-grey-400 group-hover:text-primary-600 flex-shrink-0" />
            </Link>
          </section>

          <section className="space-y-3">
            <div className="flex items-center gap-2">
              <Database className="h-5 w-5 text-grey-700" />
              <h2 className="text-lg font-semibold text-grey-900">Provider Backlog</h2>
            </div>
            <p className="text-sm text-grey-600">
              Pipeline jobs held waiting for AI provider recovery. The provider health bar
              (top of page) shows current state. Skip / replay / delete individual jobs as needed.
            </p>
            <div className="bg-surface border border-grey-200 rounded-lg p-4 shadow-sm">
              <BacklogPanel />
              <div className="mt-8">
                <ExchangeReadinessPanel />
              </div>
            </div>
          </section>

          <section className="space-y-3">
            <div className="flex items-center gap-2">
              <ImageIcon className="h-5 w-5 text-grey-700" />
              <h2 className="text-lg font-semibold text-grey-900">Article Image Remediation</h2>
            </div>
            <TextImageHeroRemediation />
          </section>

          <section className="space-y-3">
            <div className="flex items-center gap-2">
              <Scale className="h-5 w-5 text-grey-700" />
              <h2 className="text-lg font-semibold text-grey-900">Editorial Substance (recalibration gate)</h2>
            </div>
            <SubstanceRescorePreviewCard />
            <SubstanceDistributionCheckCard />
          </section>

          <section className="space-y-3">
            <div className="flex items-center gap-2">
              <Layers className="h-5 w-5 text-grey-700" />
              <h2 className="text-lg font-semibold text-grey-900">Coverage Ownership (CT-3)</h2>
            </div>
            <CoverageConsolidation />
          </section>

          <section className="space-y-3">
            <div className="flex items-center gap-2">
              <Link2 className="h-5 w-5 text-grey-700" />
              <h2 className="text-lg font-semibold text-grey-900">Entity Binding & Data Quality</h2>
            </div>
            <FirmHygiene />
            <CompetitorImportTile />
            <CompetitorSweepTile />
            <IrPrEdgeRepair />
            <EntityDriftSweep />
            <ExternalCodesBackfill />
            <EntityMismatchAudit />
            <EntityMismatchResolve />
          </section>
        </>
      )}

      {activeTab === 'archive' && (
        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <Link2 className="h-5 w-5 text-grey-700" />
            <h2 className="text-lg font-semibold text-grey-900">Entity Binding — One-off migrations</h2>
          </div>
          <CynapseBindingBackfill />
          <KeyRolePersonBackfill />
          <KeyRoleSuccession />
          <HealPersonEmbeddings />
          <PersonRoleHistoryBackfill />
          <PersonContactLinks />
          <ContactEmployment />
          <RelationshipEdges />
          <CollapseIdentities />
          <RegateMergeCandidates />
          <CanonicalNormalisation />
        </section>
      )}

      {activeTab === 'active' && (
        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <GitMerge className="h-5 w-5 text-grey-700" />
            <h2 className="text-lg font-semibold text-grey-900">Review Queues</h2>
          </div>
          <p className="text-sm text-grey-600">
            Operator-action queues populated by Cynapse's on-demand consolidation.
            A pair appears here when the matcher finds a likely duplicate but the
            similarity is below the silent-merge threshold. Approving re-keys the
            announcement links and deletes the orphan global_entity; rejecting
            records the decision so future ingests won't re-queue the pair.
          </p>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
            <MergeQueueCard kind="contacts" />
            <MergeQueueCard kind="companies" />
            <MergeQueueCard kind="funds" />
          </div>
          <IssuerLinkRepairPanel />
          <NoticeResolutionPanel />
          <SameBoardMergePanel />
          <HubBoardPanel />
        </section>
      )}

      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-grey-900">
          Maintenance Tools{activeTab === 'archive' ? ' (Archive)' : ''}
        </h2>
        {visibleTools.length === 0 && (
          <p className="text-sm text-grey-500 italic">
            No tools in this section yet.
          </p>
        )}
        {visibleTools.map((tool) => {
          const state = toolStates[tool.id] || { runState: 'idle', runId: null, dryRun: true, result: null, error: null };
          return (
            <div
              key={tool.id}
              className="bg-surface border border-grey-200 rounded-lg p-6 shadow-sm"
            >
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex items-start gap-3 flex-1">
                  <div className="mt-0.5 text-grey-500">{tool.icon}</div>
                  <div>
                    <h3 className="font-semibold text-grey-900">{tool.name}</h3>
                    <p className="text-sm text-grey-600 mt-1">{tool.description}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-wrap sm:ml-4 flex-shrink-0">
                  <button
                    onClick={() => handleRun(tool, true)}
                    disabled={state.runState === 'submitting' || state.runState === 'running'}
                    className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-grey-300 text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Eye className="h-4 w-4" />
                    Dry Run
                  </button>
                  <button
                    onClick={() => handleRun(tool, false)}
                    disabled={state.runState === 'submitting' || state.runState === 'running'}
                    className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Play className="h-4 w-4" />
                    Execute
                  </button>
                </div>
              </div>

              {/* Status / Results */}
              {state.runState !== 'idle' && (
                <div className="mt-4 pt-4 border-t border-grey-100">
                  {(state.runState === 'submitting' || state.runState === 'running') && (
                    <div className="flex items-center gap-2 text-sm text-primary-600">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>
                        {state.runState === 'submitting' ? 'Submitting...' : 'Running...'}
                        {state.dryRun && ' (dry run)'}
                      </span>
                      {state.progress && (
                        <span className="text-grey-500">&mdash; {formatProgress(state.progress)}</span>
                      )}
                    </div>
                  )}

                  {state.runState === 'completed' && state.result && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-success-700">
                        <CheckCircle className="h-4 w-4" />
                        <span>
                          {state.dryRun ? 'Dry run complete' : 'Completed'}
                        </span>
                      </div>
                      <pre className="text-xs bg-grey-50 rounded-md p-3 overflow-x-auto text-grey-800 font-mono">
                        {JSON.stringify(state.result, null, 2)}
                      </pre>
                    </div>
                  )}

                  {state.runState === 'failed' && (
                    <div className="flex items-center gap-2 text-sm text-red-600">
                      <XCircle className="h-4 w-4" />
                      <span>{state.error || 'Failed'}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Tools-page card for the merge-candidate review queues. Polls the list
 * endpoint with the pending filter (rows are slim, limit 500 covers the
 * 119-row historical backlog with headroom) and renders the live count
 * + a Review button that deep-links into MergeCandidatesPage with the
 * correct tab pre-selected.
 */
function MergeQueueCard({ kind }: { kind: 'contacts' | 'companies' | 'funds' }) {
  const label =
    kind === 'contacts' ? 'Contact Merge Candidates'
    : kind === 'companies' ? 'Company Merge Candidates'
    : 'Fund Merge Candidates';
  const Icon =
    kind === 'contacts' ? Users
    : kind === 'companies' ? Building2
    : Sparkles;
  const detail =
    kind === 'contacts'
      ? 'Likely-duplicate person rows (e.g. "T. Lowien" / "Troy Lowien" / "Mr T Lowien"). Approving re-keys announcement_contacts and unions contact_data into the canonical contact.'
    : kind === 'companies'
      ? 'Likely-duplicate company rows (e.g. "Measured Group" / "Measured Group Pty Ltd"). Approving re-keys company_announcements + entity_relationships and unions aliases.'
      : 'Likely-duplicate fund rows (ETFs / REITs / LICs / managed funds with name variants). Approving unions aliases and re-keys any fund whose manager points to the duplicate.';

  // Only the page total is read; the three lists differ in row type.
  const { data, isLoading, refetch, isFetching } = useQuery<{ total: number }>({
    queryKey: ['merge-candidates', kind, 'pending-count'],
    queryFn: () =>
      kind === 'contacts' ? listContactMergeCandidates('pending', { limit: 1 })
      : kind === 'companies' ? listCompanyMergeCandidates('pending', { limit: 1 })
      : listFundMergeCandidates('pending', { limit: 1 }),
    refetchInterval: 60_000,
  });
  const count = data?.total ?? 0;
  const countLabel = count.toLocaleString();

  return (
    <div className="bg-surface border border-grey-200 rounded-lg p-5 shadow-sm flex flex-col">
      <div className="flex items-start gap-3 flex-1">
        <div className="mt-0.5 text-grey-500"><Icon className="h-5 w-5" /></div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-grey-900">{label}</h3>
          <p className="text-sm text-grey-600 mt-1">{detail}</p>
        </div>
      </div>
      <div className="mt-4 pt-3 border-t border-grey-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin text-grey-400" />
          ) : (
            <>
              <span className={`text-2xl font-bold tabular-nums ${count > 0 ? 'text-grey-900' : 'text-grey-400'}`}>{countLabel}</span>
              <span className="text-sm text-grey-500">pending</span>
              <button
                type="button"
                onClick={() => refetch()}
                className="ml-1 text-grey-400 hover:text-grey-700"
                title="Refresh"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
              </button>
            </>
          )}
        </div>
        <Link
          to={`/entities/merge-review?tab=${kind}`}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${
            count > 0
              ? 'bg-primary-600 text-white border-primary-600 hover:bg-primary-700'
              : 'bg-surface text-grey-700 border-grey-300 hover:bg-grey-50'
          }`}
        >
          Review <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}
